<template>
    <ModalContent>
        <ClaimInfoContainer>
            <!-- 모달 헤더 -->
            <ModalHeader>
                <HeaderContent>
                    <HeaderIcon>
                        <InformationOutline :size="35" />
                    </HeaderIcon>
                    <HeaderText>
                        <ModalTitle>청구 정보 입력</ModalTitle>
                    </HeaderText>
                </HeaderContent>
            </ModalHeader>

            <!-- 메인 콘텐츠 -->
            <ClaimInfoMain>
                <!-- 왼쪽: 저장된 청구 정보 목록 -->
                <LeftSection>
                    <TitleWrapper>
                        <DataSectionTitle>청구 이력</DataSectionTitle>
                        <ButtonGroup>
                            <DeleteAllButton @click="deleteSelectedClaim" :disabled="claimHistory.length === 0">
                                <DeleteIcon :size="16" />
                                삭제
                            </DeleteAllButton>
                        </ButtonGroup>
                    </TitleWrapper>

                    <!-- 청구 이력 목록 -->
                    <ClaimHistoryList>
                        <ClaimHistoryItem v-for="(claim, index) in claimHistory" :key="index"
                            :class="{ 'active': selectedClaimIndex === index }" @click="selectClaim(claim, index)">
                            <ClaimHistoryHeader>
                                <ClaimHistoryTitle>{{ claim.title }}</ClaimHistoryTitle>
                                <ClaimHistoryStatus :status="claim.status">{{ claim.statusText }}</ClaimHistoryStatus>
                            </ClaimHistoryHeader>
                            <ClaimHistoryContent>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>유효기간:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.prescriptionPeriod }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>등록일:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>{{ claim.createdDate }}</ClaimHistoryValue>
                                </ClaimHistoryInfo>
                                <ClaimHistoryInfo>
                                    <ClaimHistoryLabel>세금계산서 / 현금영수증 발행여부:</ClaimHistoryLabel>
                                    <ClaimHistoryValue>
                                        <DocumentIssueContainer>
                                            <!-- 둘 다 미발행: 하나의 버튼만 표시 -->
                                            <template
                                                v-if="!claim.documentIssuance?.taxInvoice?.issued && !claim.documentIssuance?.cashReceipt?.issued">
                                                <IssueButton type="tax" @click.stop="openDocumentIssuanceModal(claim)">
                                                    발행
                                                </IssueButton>
                                            </template>

                                            <!-- 둘 다 발행: 발행완료 버튼 하나만 표시 -->
                                            <template
                                                v-else-if="claim.documentIssuance?.taxInvoice?.issued && claim.documentIssuance?.cashReceipt?.issued">
                                                <IssueButton type="tax" @click.stop="openDocumentIssuanceModal(claim)">
                                                    발행완료
                                                </IssueButton>
                                            </template>

                                            <!-- 하나만 발행: 각각 표시 -->
                                            <template v-else>
                                                <!-- 세금계산서 -->
                                                <DocumentStatusWrapper>
                                                    <span>{{ claim.taxInvoiceIssued }}</span>
                                                    <IssueButton v-if="!claim.documentIssuance?.taxInvoice?.issued"
                                                        type="tax" @click.stop="openDocumentIssuanceModal(claim)">
                                                        발행
                                                    </IssueButton>
                                                </DocumentStatusWrapper>

                                                <!-- 구분자 -->
                                                <Divider>/</Divider>

                                                <!-- 현금영수증 -->
                                                <DocumentStatusWrapper>
                                                    <span>{{ claim.cashReceiptIssued }}</span>
                                                    <IssueButton v-if="!claim.documentIssuance?.cashReceipt?.issued"
                                                        type="cash" @click.stop="openDocumentIssuanceModal(claim)">
                                                        발행
                                                    </IssueButton>
                                                </DocumentStatusWrapper>
                                            </template>
                                        </DocumentIssueContainer>
                                    </ClaimHistoryValue>
                                </ClaimHistoryInfo>

                            </ClaimHistoryContent>
                        </ClaimHistoryItem>

                        <!-- 청구 이력이 없는 경우 -->
                        <EmptyState v-if="claimHistory.length === 0">
                            <EmptyStateIcon>
                                <FileDocumentIcon :size="48" />
                            </EmptyStateIcon>
                            <EmptyStateTitle>청구 이력이 없습니다</EmptyStateTitle>
                            <EmptyStateDescription>새로운 청구 정보를 등록해보세요</EmptyStateDescription>
                        </EmptyState>
                    </ClaimHistoryList>
                </LeftSection>

                <!-- 오른쪽: 처방전 AI 분석 섹션 -->
                <RightSection>
                    <TitleWrapper>
                        <DataSectionTitle>처방전 AI 분석</DataSectionTitle>
                        <OcrAnalyzeButton v-if="prescriptionFile" @click="analyzePrescription"
                            :disabled="isOcrAnalyzing">
                            <OcrStatusIcon>
                                <FileDocumentIcon :size="20" />
                            </OcrStatusIcon>
                            <OcrStatusText>{{ isOcrAnalyzing ? 'OCR 분석 중...' : 'OCR 분석 시작' }}</OcrStatusText>
                        </OcrAnalyzeButton>
                    </TitleWrapper>

                    <!-- 처방전 파일 업로드 -->
                    <PrescriptionUploadContainer>
                        <PrescriptionDropZone ref="prescriptionDropZone" :class="{
                            'active': isPrescriptionDragging,
                            'has-file': prescriptionFile
                        }" @dragenter="handlePrescriptionDragEnter" @dragover="handlePrescriptionDragOver"
                            @dragleave="handlePrescriptionDragLeave" @drop="handlePrescriptionDrop"
                            @paste="handlePrescriptionPaste" @click="triggerPrescriptionFileInput" tabindex="0">

                            <PrescriptionUploadArea v-if="!prescriptionFile">
                                <PrescriptionUploadIcon>
                                    <FileDocumentIcon :size="30" />
                                </PrescriptionUploadIcon>
                                <PrescriptionUploadText>
                                    <PrescriptionUploadMainText>파일을 드래그하거나 클릭하여 업로드</PrescriptionUploadMainText>
                                    <PrescriptionUploadSubText>PDF, JPG, PNG 파일 지원</PrescriptionUploadSubText>
                                </PrescriptionUploadText>
                            </PrescriptionUploadArea>

                            <PrescriptionFilePreview v-else>
                                <PrescriptionFileIcon>
                                    <FileDocumentIcon :size="32" />
                                </PrescriptionFileIcon>
                                <PrescriptionFileInfo>
                                    <PrescriptionFileName>{{ prescriptionFile.name }}</PrescriptionFileName>
                                    <PrescriptionFileSize>{{ formatFileSize(prescriptionFile.size) }}
                                    </PrescriptionFileSize>
                                </PrescriptionFileInfo>
                                <PrescriptionFileRemove @click.stop="removePrescriptionFile">
                                    <CloseIcon :size="16" :style="{ paddingTop: '3px' }" />
                                </PrescriptionFileRemove>
                            </PrescriptionFilePreview>

                            <input ref="prescriptionFileInput" type="file" accept=".pdf,.jpg,.jpeg,.png"
                                style="display: none" @change="handlePrescriptionFileChange" />
                        </PrescriptionDropZone>
                    </PrescriptionUploadContainer>

                    <!-- 처방전 폼 -->
                    <PrescriptionForm v-if="showOxygenOptions">
                        <FormRow>
                            <FormGroup>
                                <FormLabel>공인인증서 사용자이름</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.certificateName"
                                    placeholder="공인인증서 사용자이름을 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>공인인증서 비밀번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.cert_password" type="password"
                                    placeholder="공인인증서 비밀번호를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>성명</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.name" placeholder="성명을 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>주민등록번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.residentNumber"
                                    @input="formatResidentNumber" placeholder="주민등록번호를 입력하세요" maxlength="14" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>전화번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.phone" @input="formatPhoneNumber"
                                    placeholder="전화번호를 입력하세요" maxlength="13" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>상병명</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.diseaseName" placeholder="상병명을 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>상병코드</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.diseaseCode"
                                    @input="prescriptionData.diseaseCode = $event.target.value.toUpperCase()"
                                    placeholder="상병코드를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>의사 면허번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.doctorLicense"
                                    placeholder="의사 면허번호를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>전문의 자격번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.specialistLicense"
                                    placeholder="전문의 자격번호를 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>산소치료 처방일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.oxygenDate" placeholder="처방일을 선택하세요"
                                    style="width: 100%;" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>처방기간 시작일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.oxygenSdate" placeholder="시작일을 선택하세요"
                                    style="width: 100%;" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>처방기간 종료일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.oxygenEdate" placeholder="종료일을 선택하세요"
                                    style="width: 100%;" :lang="ko" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>접수구분</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.receiptType">
                                    <option value="">접수구분을 선택하세요</option>
                                    <option value="가정용">가정용</option>
                                    <option value="휴대용">휴대용</option>
                                    <option value="동시접수">동시접수</option>
                                </PrescriptionSelect>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>처방구분</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.issuanceType">
                                    <option value="">처방구분을 선택하세요</option>
                                    <option value="최초">최초</option>
                                    <option value="재진">재진</option>
                                </PrescriptionSelect>
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>가정용 산소번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.oxygenHomeGenerator"
                                    placeholder="가정용 산소번호를 입력하세요" :disabled="!isHomeOxygenEnabled" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>휴대용 산소번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.oxygenMobileGenerator"
                                    placeholder="휴대용 산소번호를 입력하세요" :disabled="!isMobileOxygenEnabled" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>가정용 사용기간 시작일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.useHomeStartDate" placeholder="시작일을 선택하세요"
                                    style="width: 100%;" :disabled="!isHomeOxygenEnabled" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>가정용 사용기간 종료일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.useHomeEndDate" placeholder="종료일을 선택하세요"
                                    style="width: 100%;" :disabled="!isHomeOxygenEnabled" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>가정용 청구차수</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.homeBillingCount"
                                    :disabled="!isHomeOxygenEnabled">
                                    <option value="">청구차수를 선택하세요</option>
                                    <option v-for="i in 24" :key="i" :value="`${i}차`">{{ i }}차</option>
                                </PrescriptionSelect>
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>휴대용 사용기간 시작일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.useMobileStartDate" placeholder="시작일을 선택하세요"
                                    style="width: 100%;" :disabled="!isMobileOxygenEnabled" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>휴대용 사용기간 종료일</FormLabel>
                                <Datepicker v-model:value="prescriptionData.useMobileEndDate" placeholder="종료일을 선택하세요"
                                    style="width: 100%;" :disabled="!isMobileOxygenEnabled" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>휴대용 청구차수</FormLabel>
                                <PrescriptionSelect v-model="prescriptionData.mobileBillingCount"
                                    :disabled="!isMobileOxygenEnabled">
                                    <option value="">청구차수를 선택하세요</option>
                                    <option v-for="i in 24" :key="i" :value="`${i}차`">{{ i }}차</option>
                                </PrescriptionSelect>
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>현금영수증 승인번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.cashReceipt"
                                    placeholder="현금영수증 승인번호 24자리를 입력하세요" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>현금영수증 작성일자</FormLabel>
                                <Datepicker v-model:value="prescriptionData.cashReceiptDate" placeholder="작성일자를 선택하세요"
                                    style="width: 100%;" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>현금영수증 합계금액</FormLabel>
                                <PrescriptionInput :value="formatPrice(prescriptionData.cashReceiptAmount)"
                                    placeholder="현금영수증 합계금액을 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <FormRow>
                            <FormGroup>
                                <FormLabel>세금계산서 승인번호</FormLabel>
                                <PrescriptionInput v-model="prescriptionData.taxInvoice"
                                    placeholder="세금계산서 승인번호 24자리를 입력하세요" maxlength="24" required />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>세금계산서 작성일자</FormLabel>
                                <Datepicker v-model:value="prescriptionData.taxInvoiceDate" placeholder="작성일자를 선택하세요"
                                    style="width: 100%;" :lang="ko" />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>세금계산서 합계금액</FormLabel>
                                <PrescriptionInput :value="formatPrice(prescriptionData.taxInvoiceAmount)"
                                    placeholder="세금계산서 합계금액을 입력하세요" />
                            </FormGroup>
                        </FormRow>

                        <!-- 동맥혈 가스검사 결과 섹션 -->
                        <FormSection>
                            <FormSectionTitle>동맥혈 가스검사 결과</FormSectionTitle>
                            <FormRow>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem1"
                                        :checked="prescriptionData.bloodGasItem1"
                                        @change="updateCheckbox('bloodGasItem1', $event)" />
                                    <CheckboxLabel for="bloodGasItem1">산소분압 (PaO₂) 55mmHg 이하</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem2"
                                        :checked="prescriptionData.bloodGasItem2"
                                        @change="updateCheckbox('bloodGasItem2', $event)" />
                                    <CheckboxLabel for="bloodGasItem2">산소포화도 (SaO₂) 88% 이하</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem3"
                                        :checked="prescriptionData.bloodGasItem3"
                                        @change="updateCheckbox('bloodGasItem3', $event)" />
                                    <CheckboxLabel for="bloodGasItem3">산소분압 (PaO₂) 56~59mmHg</CheckboxLabel>
                                </CheckboxFormGroup>
                            </FormRow>
                            <FormRow>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem4"
                                        :checked="prescriptionData.bloodGasItem4"
                                        @change="updateCheckbox('bloodGasItem4', $event)" />
                                    <CheckboxLabel for="bloodGasItem4">산소포화도 (SaO₂) 89% 이상</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem5"
                                        :checked="prescriptionData.bloodGasItem5"
                                        @change="updateCheckbox('bloodGasItem5', $event)" />
                                    <CheckboxLabel for="bloodGasItem5">적혈구 증가증 (헤마토크릿 55% 초과)</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem6"
                                        :checked="prescriptionData.bloodGasItem6"
                                        @change="updateCheckbox('bloodGasItem6', $event)" />
                                    <CheckboxLabel for="bloodGasItem6">울혈성 심부전을 시사하는 말초부종</CheckboxLabel>
                                </CheckboxFormGroup>
                            </FormRow>
                            <FormRow>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="bloodGasItem7"
                                        :checked="prescriptionData.bloodGasItem7"
                                        @change="updateCheckbox('bloodGasItem7', $event)" />
                                    <CheckboxLabel for="bloodGasItem7">폐동맥고혈압</CheckboxLabel>
                                </CheckboxFormGroup>
                            </FormRow>
                        </FormSection>

                        <!-- 산소포화도 검사결과 섹션 -->
                        <FormSection>
                            <FormSectionTitle>산소포화도 검사결과</FormSectionTitle>
                            <FormRow>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="oxygenSaturationItem1"
                                        :checked="prescriptionData.oxygenSaturationItem1"
                                        @change="updateCheckbox('oxygenSaturationItem1', $event)" />
                                    <CheckboxLabel for="oxygenSaturationItem1">산소포화도가 88% 이하</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="oxygenSaturationItem2"
                                        :checked="prescriptionData.oxygenSaturationItem2"
                                        @change="updateCheckbox('oxygenSaturationItem2', $event)" />
                                    <CheckboxLabel for="oxygenSaturationItem2">산소포화도가 89% 이상</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="oxygenSaturationItem3"
                                        :checked="prescriptionData.oxygenSaturationItem3"
                                        @change="updateCheckbox('oxygenSaturationItem3', $event)" />
                                    <CheckboxLabel for="oxygenSaturationItem3">적혈구 증가증(헤마토크릿 55% 초과)</CheckboxLabel>
                                </CheckboxFormGroup>
                            </FormRow>
                            <FormRow>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="oxygenSaturationItem4"
                                        :checked="prescriptionData.oxygenSaturationItem4"
                                        @change="updateCheckbox('oxygenSaturationItem4', $event)" />
                                    <CheckboxLabel for="oxygenSaturationItem4">울혈성 심부전을 시사하는 말초부종</CheckboxLabel>
                                </CheckboxFormGroup>
                                <CheckboxFormGroup>
                                    <PrescriptionCheckbox type="checkbox" id="oxygenSaturationItem5"
                                        :checked="prescriptionData.oxygenSaturationItem5"
                                        @change="updateCheckbox('oxygenSaturationItem5', $event)" />
                                    <CheckboxLabel for="oxygenSaturationItem5">폐동맥고혈압</CheckboxLabel>
                                </CheckboxFormGroup>
                            </FormRow>
                        </FormSection>
                    </PrescriptionForm>
                </RightSection>

                <!-- 오른쪽: 처방전 미리보기 섹션 -->
                <PreviewSection v-if="prescriptionFile">
                    <TitleWrapper>
                        <DataSectionTitle>처방전 미리보기</DataSectionTitle>
                        <PreviewControls>
                            <ZoomButton @click="zoomOut" :disabled="zoomLevel <= 0.1">
                                <ZoomOutIcon :size="16" />
                            </ZoomButton>
                            <ZoomLevel>{{ Math.round(zoomLevel * 100) }}%</ZoomLevel>
                            <ZoomButton @click="zoomIn" :disabled="zoomLevel >= 3">
                                <ZoomInIcon :size="16" />
                            </ZoomButton>
                            <ResetZoomButton @click="resetZoom">
                                <RefreshIcon :size="16" />
                                리셋
                            </ResetZoomButton>
                        </PreviewControls>
                    </TitleWrapper>

                    <PreviewContainer>
                        <div v-if="isImageLoading" class="loading-container">
                            <div class="loading-spinner"></div>
                            <p>이미지 로딩 중...</p>
                        </div>
                        <div v-else-if="imageError" class="error-container">
                            <p>이미지를 불러올 수 없습니다.</p>
                        </div>
                        <PreviewImage v-else-if="prescriptionPreviewUrl" :src="prescriptionPreviewUrl" :style="{
                            transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                            transformOrigin: '0 0'
                        }" @load="onImageLoad" @error="onImageError" @mousedown="onMouseDown"
                            @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd" />
                    </PreviewContainer>
                </PreviewSection>
            </ClaimInfoMain>

            <!-- 하단 버튼 -->
            <ClaimInfoFooter>
                <CancelButton @click="$emit('close')">취소</CancelButton>
                <CancelButton @click="copySelectedClaim" :disabled="selectedClaimIndex < 0">복사</CancelButton>
                <SaveButton @click="saveClaimInfo">
                    {{ selectedClaimIndex >= 0 ? '저장' : '등록' }}
                </SaveButton>
            </ClaimInfoFooter>
        </ClaimInfoContainer>

        <!-- * 문서 발행 모달 -->
        <ModalPage v-model:show="showDocumentIssuanceModal" :width="'55%'" :height="'60%'">
            <DocumentIssuanceModal v-if="showDocumentIssuanceModal && currentClaimForDocument" :orderId="orderId"
                :orderData="currentClaimForDocument?.data?.prescriptionData || orderData" :claimType="claimType"
                :products="products" @close="closeDocumentIssuanceModal" @document-issued="handleDocumentIssued" />
        </ModalPage>
    </ModalContent>
</template>

<script>
import ModalPage from '@/components/layout/ModalPage.vue';
import * as ClaimInfoModalCss from '@/assets/styles/order/modal/ClaimInfoModalCss.js';
import DocumentIssuanceModal from '@/components/module/DocumentIssuanceModal.vue';

import CloseIcon from 'vue-material-design-icons/Close.vue';
import FileDocumentIcon from 'vue-material-design-icons/FileDocument.vue';
import RefreshIcon from 'vue-material-design-icons/Refresh.vue';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import EditIcon from 'vue-material-design-icons/Pencil.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';
import Datepicker from 'vue-datepicker-next';
import InformationOutline from 'vue-material-design-icons/InformationOutline.vue';
import ZoomInIcon from 'vue-material-design-icons/MagnifyPlus.vue';
import ZoomOutIcon from 'vue-material-design-icons/MagnifyMinus.vue';

import * as pdfjsLib from 'pdfjs-dist';

export default {
    name: 'ClaimInfoModal',

    components: {
        ...ClaimInfoModalCss,
        ModalPage,
        DocumentIssuanceModal,
        CloseIcon,
        FileDocumentIcon,
        RefreshIcon,
        PlusIcon,
        EditIcon,
        DeleteIcon,
        Datepicker,
        InformationOutline,
        ZoomInIcon,
        ZoomOutIcon
    },

    props: {
        orderId: {
            type: [String, Number],
            required: true
        },
        orderData: {
            type: Object,
            default: () => ({})
        },
        claimType: {
            type: String,
            default: 'oxygen'
        },
        products: {
            type: Array,
            default: () => []
        },
        rentalTypes: {
            type: Object,
            default: () => ({
                hasRentalProduct: false,
                hasOxygenGenerator: false,
                hasDiabetesProduct: false,
                hasAssistanceProduct: false
            })
        },
        memberData: {
            type: Object,
            default: () => ({})
        },
        additionalData: {
            type: Object,
            default: () => ({})
        }
    },

    emits: ['close', 'claim-info-saved', 'prescription-file-uploaded'],

    setup() {
        // Electron 환경 감지 및 worker 설정
        const isElectron = typeof window !== 'undefined' &&
            (window.electronAPI?.isElectron ||
                (window.process && window.process.type === 'renderer') ||
                (typeof navigator !== 'undefined' && navigator.userAgent.indexOf('Electron') >= 0));

        const isProduction = process.env.NODE_ENV === 'production';

        if (pdfjsLib.GlobalWorkerOptions) {
            if (isElectron || isProduction) {
                // Electron 배포 환경: 로컬 worker 파일 사용
                // node_modules에서 직접 경로 지정
                pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
                    'pdfjs-dist/build/pdf.worker.min.js',
                    import.meta.url
                ).toString();
            } else {
                // 개발 환경: CDN 사용
                pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
            }
        }

        const ko = {
            formatLocale: {
                months: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                monthsShort: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
                weekdays: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
                weekdaysShort: ['일', '월', '화', '수', '목', '금', '토'],
                weekdaysMin: ['일', '월', '화', '수', '목', '금', '토'],
                firstDayOfWeek: 0,
                firstWeekContainsDate: 1,
                meridiem: (h) => (h < 12 ? '오전' : '오후'),
                meridiemParse: /오전|오후/,
                isPM: (input) => input === '오후'
            },
            yearFormat: 'YYYY년',
            monthFormat: 'MMM',
            monthBeforeYear: false
        };

        return {
            ko
        };
    },

    data() {
        return {
            prescriptionFile: null,
            isPrescriptionDragging: false,
            showDocumentIssuanceModal: false, // DocumentIssuanceModal 표시 여부
            currentClaimForDocument: null, // 현재 문서 발행할 청구 데이터
            showOxygenOptions: true,
            selectedClaimIndex: -1,
            isOcrAnalyzing: false,
            ocrDataArray: null, // OCR 데이터 배열 저장용 추가

            // oxygen_type에 따른 필드 활성화 상태
            isHomeOxygenEnabled: false,
            isMobileOxygenEnabled: false,

            // 청구 이력 데이터
            claimHistory: [],

            prescriptionData: {
                certificateName: '',
                cert_password: '',
                name: '',
                residentNumber: '',
                phone: '',
                diseaseName: '',
                diseaseCode: '',
                doctorLicense: '',
                specialistLicense: '',
                oxygenSdate: null,
                oxygenEdate: null,
                oxygenDate: null,
                receiptType: '',
                issuanceType: '',
                useHomeStartDate: null,
                useHomeEndDate: null,
                useMobileStartDate: null,
                useMobileEndDate: null,
                oxygenHomeGenerator: '',
                oxygenMobileGenerator: '',
                homeBillingCount: '',
                mobileBillingCount: '',
                cashReceipt: '',
                cashReceiptDate: null,
                cashReceiptAmount: '',
                taxInvoice: '',
                taxInvoiceDate: null,
                taxInvoiceAmount: '',
                bloodGasItem1: false,
                bloodGasItem2: false,
                bloodGasItem3: false,
                bloodGasItem4: false,
                bloodGasItem5: false,
                bloodGasItem6: false,
                bloodGasItem7: false,
                oxygenSaturationItem1: false,
                oxygenSaturationItem2: false,
                oxygenSaturationItem3: false,
                oxygenSaturationItem4: false,
                oxygenSaturationItem5: false
            },
            // 미리보기 관련 데이터 추가
            prescriptionPreviewUrl: null,
            zoomLevel: 1,
            isImageLoaded: false,
            imageError: false,
            isImageLoading: false,
            // 드래그 관련 상태
            isDragging: false,
            dragStart: { x: 0, y: 0 },
            imagePosition: { x: 0, y: 0 },
            lastImagePosition: { x: 0, y: 0 }
        };
    },

    mounted() {
        this.initializeData();
        this.loadClaimHistory();
    },

    methods: {
        // 문서 발행 모달 열기
        openDocumentIssuanceModal(claim) {
            this.currentClaimForDocument = claim;
            this.showDocumentIssuanceModal = true;
        },

        // 문서 발행 모달 닫기
        closeDocumentIssuanceModal() {
            this.showDocumentIssuanceModal = false;
            this.currentClaimForDocument = null;
            // 청구 이력 새로고침
            this.loadClaimHistory();
        },

        // 문서 발행 완료 이벤트 처리
        handleDocumentIssued(event) {
            // 청구 이력 새로고침
            this.loadClaimHistory();
        },

        // 마우스 다운 이벤트 (드래그 시작)
        onMouseDown(event) {
            if (!this.prescriptionPreviewUrl) return;

            this.isDragging = true;
            this.dragStart = {
                x: event.clientX - this.imagePosition.x,
                y: event.clientY - this.imagePosition.y
            };

            // 마우스 커서 변경
            event.target.style.cursor = 'grabbing';

            // 전역 이벤트 리스너 추가
            document.addEventListener('mousemove', this.onMouseMove);
            document.addEventListener('mouseup', this.onMouseUp);

            // 기본 동작 방지
            event.preventDefault();
        },

        // 마우스 이동 이벤트 (드래그 중)
        onMouseMove(event) {
            if (!this.isDragging) return;

            this.imagePosition = {
                x: event.clientX - this.dragStart.x,
                y: event.clientY - this.dragStart.y
            };
        },

        // 마우스 업 이벤트 (드래그 종료)
        onMouseUp(event) {
            if (!this.isDragging) return;

            this.isDragging = false;
            this.lastImagePosition = { ...this.imagePosition };

            // 마우스 커서 복원
            event.target.style.cursor = 'grab';

            // 전역 이벤트 리스너 제거
            document.removeEventListener('mousemove', this.onMouseMove);
            document.removeEventListener('mouseup', this.onMouseUp);
        },

        // 터치 시작 이벤트 (모바일 지원)
        onTouchStart(event) {
            if (!this.prescriptionPreviewUrl) return;

            const touch = event.touches[0];
            this.isDragging = true;
            this.dragStart = {
                x: touch.clientX - this.imagePosition.x,
                y: touch.clientY - this.imagePosition.y
            };

            // 기본 동작 방지
            event.preventDefault();
        },

        // 터치 이동 이벤트 (모바일 지원)
        onTouchMove(event) {
            if (!this.isDragging) return;

            const touch = event.touches[0];
            this.imagePosition = {
                x: touch.clientX - this.dragStart.x,
                y: touch.clientY - this.dragStart.y
            };

            // 기본 동작 방지
            event.preventDefault();
        },

        // 터치 종료 이벤트 (모바일 지원)
        onTouchEnd(event) {
            if (!this.isDragging) return;

            this.isDragging = false;
            this.lastImagePosition = { ...this.imagePosition };
        },

        // 이미지 위치 리셋
        resetImagePosition() {
            this.imagePosition = { x: 0, y: 0 };
            this.lastImagePosition = { x: 0, y: 0 };
        },

        // 처방전 파일 처리 시 미리보기 URL 생성 (수정)
        handlePrescriptionFile(file) {
            if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
                this.prescriptionFile = file;
                this.isImageLoading = true;
                this.imageError = false;

                // 부모 컴포넌트에 처방전 파일 업로드 이벤트 즉시 전달
                this.$emit('prescription-file-uploaded', file);

                // 미리보기 URL 생성
                this.createPreviewUrl(file);
            } else {
                this.$swal.fire({
                    icon: 'error',
                    title: '파일 형식 오류',
                    text: 'PDF, JPG, PNG 파일만 업로드 가능합니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 미리보기 URL 생성 (수정)
        createPreviewUrl(file) {
            // 기존 URL 해제
            if (this.prescriptionPreviewUrl) {
                URL.revokeObjectURL(this.prescriptionPreviewUrl);
            }

            if (file.type === 'application/pdf') {
                // PDF의 경우 첫 번째 페이지를 이미지로 변환
                this.convertPdfToImage(file);
            } else {
                // 이미지 파일의 경우 직접 URL 생성
                this.prescriptionPreviewUrl = URL.createObjectURL(file);
                this.isImageLoading = false;
                this.imageError = false;
            }
        },

        // PDF를 이미지로 변환 (OrderModifyPage.vue 방식으로 수정)
        async convertPdfToImage(file) {
            this.isImageLoading = true;
            this.imageError = false;

            try {
                // PDF 파일을 ArrayBuffer로 변환
                const arrayBuffer = await file.arrayBuffer();

                // PDF 문서 로드 (OrderModifyPage.vue와 동일한 방식)
                const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;

                // 첫 번째 페이지 가져오기
                const page = await pdf.getPage(1);

                // 캔버스 생성
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                // 페이지 크기 설정 (해상도 조정 가능)
                const viewport = page.getViewport({ scale: 2.0 });
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                // 페이지 렌더링
                await page.render({
                    canvasContext: context,
                    viewport: viewport
                }).promise;

                // 캔버스를 Blob으로 변환
                return new Promise((resolve, reject) => {
                    canvas.toBlob((blob) => {
                        if (blob) {
                            // 기존 URL 해제
                            if (this.prescriptionPreviewUrl) {
                                URL.revokeObjectURL(this.prescriptionPreviewUrl);
                            }

                            this.prescriptionPreviewUrl = URL.createObjectURL(blob);
                            this.isImageLoading = false;
                            this.imageError = false;
                            resolve(blob);
                        } else {
                            reject(new Error('Blob 생성 실패'));
                        }
                    }, 'image/png', 0.95);
                });

            } catch (error) {
                console.error('PDF 변환 오류:', error);
                this.isImageLoading = false;
                this.imageError = true;
                this.$swal.fire({
                    icon: 'error',
                    title: 'PDF 변환 실패',
                    text: error.message || 'PDF 파일을 이미지로 변환하는 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
                throw error;
            }
        },

        // 이미지 로드 완료
        onImageLoad(event) {
            this.isImageLoaded = true;
            this.imageError = false;
            this.isImageLoading = false;

            // 이미지가 컨테이너에 맞게 자동으로 조정
            this.$nextTick(() => {
                this.fitImageToContainer(event.target);
            });
        },

        // 이미지를 컨테이너에 맞게 자동 조정
        fitImageToContainer(imgElement) {
            if (!imgElement) return;

            // 컨테이너 크기 가져오기
            const container = this.$el?.querySelector('.preview-container') ||
                document.querySelector('.preview-container');

            if (!container) {
                // 컨테이너를 찾을 수 없으면 ref 사용
                const previewSection = this.$el?.querySelector('[class*="PreviewContainer"]');
                if (previewSection) {
                    const containerWidth = previewSection.clientWidth;
                    const containerHeight = previewSection.clientHeight || 600; // 기본 높이 600px

                    // 이미지 원본 크기
                    const imgWidth = imgElement.naturalWidth || imgElement.width;
                    const imgHeight = imgElement.naturalHeight || imgElement.height;

                    if (imgWidth && imgHeight) {
                        // 컨테이너에 맞게 줌 레벨 계산 (여유 공간 20px)
                        const scaleX = (containerWidth - 40) / imgWidth;
                        const scaleY = (containerHeight - 40) / imgHeight;
                        const autoZoom = Math.min(scaleX, scaleY, 1); // 1을 넘지 않도록

                        // 줌 레벨 설정
                        this.zoomLevel = Math.max(0.1, autoZoom);

                        // 이미지 위치 중앙 정렬
                        const scaledWidth = imgWidth * this.zoomLevel;
                        const scaledHeight = imgHeight * this.zoomLevel;
                        this.imagePosition = {
                            x: (containerWidth - scaledWidth) / 2,
                            y: (containerHeight - scaledHeight) / 2
                        };
                    }
                }
            } else {
                const containerWidth = container.clientWidth;
                const containerHeight = container.clientHeight || 600;

                // 이미지 원본 크기
                const imgWidth = imgElement.naturalWidth || imgElement.width;
                const imgHeight = imgElement.naturalHeight || imgElement.height;

                if (imgWidth && imgHeight) {
                    // 컨테이너에 맞게 줌 레벨 계산 (여유 공간 20px)
                    const scaleX = (containerWidth - 40) / imgWidth;
                    const scaleY = (containerHeight - 40) / imgHeight;
                    const autoZoom = Math.min(scaleX, scaleY, 1); // 1을 넘지 않도록

                    // 줌 레벨 설정
                    this.zoomLevel = Math.max(0.1, autoZoom);

                    // 이미지 위치 중앙 정렬
                    const scaledWidth = imgWidth * this.zoomLevel;
                    const scaledHeight = imgHeight * this.zoomLevel;
                    this.imagePosition = {
                        x: (containerWidth - scaledWidth) / 2,
                        y: (containerHeight - scaledHeight) / 2
                    };
                }
            }
        },

        // 이미지 로드 오류
        onImageError() {
            this.imageError = true;
            this.isImageLoaded = false;
            this.isImageLoading = false;
        },

        // 줌 인
        zoomIn() {
            if (this.zoomLevel < 3) {
                this.zoomLevel = Math.min(3, this.zoomLevel + 0.05);
            }
        },

        // 줌 아웃
        zoomOut() {
            if (this.zoomLevel > 0.01) {
                this.zoomLevel = Math.max(0.01, this.zoomLevel - 0.05);
            }
        },

        // 줌 리셋
        resetZoom() {
            this.zoomLevel = 1;
            this.resetImagePosition();

            // 이미지가 있으면 다시 fit-to-container 적용
            if (this.prescriptionPreviewUrl) {
                this.$nextTick(() => {
                    const imgElement = this.$el?.querySelector('[class*="PreviewImage"]');
                    if (imgElement) {
                        this.fitImageToContainer(imgElement);
                    }
                });
            }
        },

        // 처방전 파일 제거 시 미리보기도 제거
        removePrescriptionFile() {
            if (this.prescriptionPreviewUrl) {
                URL.revokeObjectURL(this.prescriptionPreviewUrl);
                this.prescriptionPreviewUrl = null;
            }
            this.prescriptionFile = null;
            this.zoomLevel = 1;
            this.isImageLoaded = false;
            this.imageError = false;
            this.isImageLoading = false;
            this.resetImagePosition();
        },

        // 선택된 청구 이력 복사
        copySelectedClaim() {
            if (this.selectedClaimIndex < 0) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '청구를 선택해주세요',
                    text: '복사할 청구 이력을 먼저 선택해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            const selectedClaim = this.claimHistory[this.selectedClaimIndex];

            this.$swal.fire({
                title: '청구 복사',
                text: `"${selectedClaim.title}" 청구 데이터를 폼에 복사하시겠습니까?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '복사',
                cancelButtonText: '취소',
                confirmButtonColor: '#0f4c5c',
                cancelButtonColor: '#6b7280'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.copyClaimDataToForm(selectedClaim);
                    this.$swal.fire({
                        icon: 'success',
                        title: '복사 완료',
                        text: '청구 데이터가 폼에 복사되었습니다.',
                        confirmButtonText: '확인'
                    });
                }
            });
        },

        // 청구 데이터를 폼에 복사
        copyClaimDataToForm(claim) {
            if (!claim.data || !claim.data.prescriptionData) {
                console.error('복사할 청구 데이터가 없습니다:', claim);
                return;
            }

            const originalData = claim.data.prescriptionData;

            // Vue의 반응성을 위해 $nextTick 사용
            this.$nextTick(() => {
                // 기본 정보 복사
                this.prescriptionData.certificateName = originalData.certificateName || '';
                this.prescriptionData.cert_password = originalData.cert_password || '';
                this.prescriptionData.name = originalData.name || '';
                this.prescriptionData.residentNumber = originalData.residentNumber || '';
                this.prescriptionData.phone = originalData.phone || '';
                this.prescriptionData.diseaseName = originalData.diseaseName || '';
                this.prescriptionData.diseaseCode = originalData.diseaseCode || '';
                this.prescriptionData.doctorLicense = originalData.doctorLicense || '';
                this.prescriptionData.specialistLicense = originalData.specialistLicense || '';

                // 날짜 정보 복사
                this.prescriptionData.oxygenSdate = originalData.oxygenSdate || null;
                this.prescriptionData.oxygenEdate = originalData.oxygenEdate || null;
                this.prescriptionData.oxygenDate = originalData.oxygenDate || null;

                // 접수구분 및 처방구분 복사
                this.prescriptionData.receiptType = originalData.receiptType || '';
                this.prescriptionData.issuanceType = originalData.issuanceType || '';

                // 사용기간 정보 복사
                this.prescriptionData.useHomeStartDate = originalData.useHomeStartDate || null;
                this.prescriptionData.useHomeEndDate = originalData.useHomeEndDate || null;
                this.prescriptionData.useMobileStartDate = originalData.useMobileStartDate || null;
                this.prescriptionData.useMobileEndDate = originalData.useMobileEndDate || null;

                // 산소번호 및 청구차수 복사
                this.prescriptionData.oxygenHomeGenerator = originalData.oxygenHomeGenerator || '';
                this.prescriptionData.oxygenMobileGenerator = originalData.oxygenMobileGenerator || '';
                this.prescriptionData.homeBillingCount = originalData.homeBillingCount || '';
                this.prescriptionData.mobileBillingCount = originalData.mobileBillingCount || '';

                // 세금계산서 및 현금영수증 승인번호는 복사하지 않음 (빈 값으로 설정)
                this.prescriptionData.cashReceipt = '';
                this.prescriptionData.cashReceiptDate = null;
                this.prescriptionData.cashReceiptAmount = '';
                this.prescriptionData.taxInvoice = '';
                this.prescriptionData.taxInvoiceDate = null;
                this.prescriptionData.taxInvoiceAmount = '';

                // 체크박스 정보 복사
                this.prescriptionData.bloodGasItem1 = Boolean(originalData.bloodGasItem1);
                this.prescriptionData.bloodGasItem2 = Boolean(originalData.bloodGasItem2);
                this.prescriptionData.bloodGasItem3 = Boolean(originalData.bloodGasItem3);
                this.prescriptionData.bloodGasItem4 = Boolean(originalData.bloodGasItem5);
                this.prescriptionData.bloodGasItem5 = Boolean(originalData.bloodGasItem5);
                this.prescriptionData.bloodGasItem6 = Boolean(originalData.bloodGasItem6);
                this.prescriptionData.bloodGasItem7 = Boolean(originalData.bloodGasItem7);

                this.prescriptionData.oxygenSaturationItem1 = Boolean(originalData.oxygenSaturationItem1);
                this.prescriptionData.oxygenSaturationItem2 = Boolean(originalData.oxygenSaturationItem2);
                this.prescriptionData.oxygenSaturationItem3 = Boolean(originalData.oxygenSaturationItem3);
                this.prescriptionData.oxygenSaturationItem4 = Boolean(originalData.oxygenSaturationItem4);
                this.prescriptionData.oxygenSaturationItem5 = Boolean(originalData.oxygenSaturationItem5);

                // status 값 복사
                // 청구완료 상태인 경우 청구등록 상태로 변경하여 복사
                if (claim.statusText === '청구완료') {
                    this.prescriptionData.status = '청구등록';
                } else {
                    this.prescriptionData.status = claim.statusText;
                }

                // statusText가 '청구등록'인 경우 날짜를 한 달씩 늘려주고 청구차수 +1
                if (claim.statusText === '청구등록' || claim.statusText === '청구완료') {
                    this.extendDatesByOneMonth();
                    this.incrementBillingCount();
                } else {
                    console.log('청구등록 상태가 아님:', claim.statusText);
                }

                // oxygen_type에 따른 필드 활성화 설정
                this.setOxygenTypeConfiguration(originalData.receiptType);

                // 선택 해제 (새로운 청구로 취급)
                this.selectedClaimIndex = -1;

                // 강제로 UI 업데이트 트리거
                this.$forceUpdate();
            });
        },

        // 날짜를 한 달씩 늘려주는 헬퍼 메서드
        extendDatesByOneMonth() {
            const addOneMonth = (date) => {
                if (!date) {
                    return null;
                }

                // Date 객체로 변환
                let dateObj;
                if (date instanceof Date) {
                    dateObj = new Date(date);
                } else if (typeof date === 'string') {
                    dateObj = new Date(date);
                } else {
                    return null;
                }

                // 유효한 날짜인지 확인
                if (isNaN(dateObj.getTime())) {
                    return null;
                }

                const newDate = new Date(dateObj);
                newDate.setMonth(newDate.getMonth() + 1);
                return newDate;
            };

            this.prescriptionData.useHomeStartDate = addOneMonth(this.prescriptionData.useHomeStartDate);
            this.prescriptionData.useHomeEndDate = addOneMonth(this.prescriptionData.useHomeEndDate);
            this.prescriptionData.useMobileStartDate = addOneMonth(this.prescriptionData.useMobileStartDate);
            this.prescriptionData.useMobileEndDate = addOneMonth(this.prescriptionData.useMobileEndDate);
        },

        // 청구차수를 +1 해주는 헬퍼 메서드
        incrementBillingCount() {
            const incrementCount = (count) => {
                if (!count || count === '') {
                    return '1차';
                }

                // '차' 문자 제거하고 숫자만 추출
                const cleanCount = count.replace('차', '');
                const numCount = parseInt(cleanCount);

                if (isNaN(numCount)) {
                    return '1차';
                }

                const newCount = String(numCount + 1) + '차';
                return newCount;
            };

            // 홈 청구차수와 모바일 청구차수를 +1
            this.prescriptionData.homeBillingCount = incrementCount(this.prescriptionData.homeBillingCount);
            this.prescriptionData.mobileBillingCount = incrementCount(this.prescriptionData.mobileBillingCount);
        },

        // 체크박스 상태 업데이트 메서드
        updateCheckbox(fieldName, event) {
            this.prescriptionData[fieldName] = event.target.checked;
        },

        initializeData() {
            // 주문 데이터에서 기본 정보 설정
            if (this.orderData) {
                // 수급자 기본 정보
                this.prescriptionData.name = this.orderData.name || '';
                this.prescriptionData.phone = this.formatPhoneWithHyphens(this.orderData.phone1 || '');
                this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(this.orderData.number || '');
            }

            // 추가 데이터에서 정보 추출
            if (this.additionalData) {
                const clientDetails = this.additionalData.clientDetails || {};
                const productInfo = this.additionalData.productInfo || {};
                const claimInfo = this.additionalData.claimInfo || {};

                // 수급자 상세 정보
                if (clientDetails.name && !this.prescriptionData.name) {
                    this.prescriptionData.name = clientDetails.name;
                }
                if (clientDetails.phone1 && !this.prescriptionData.phone) {
                    this.prescriptionData.phone = this.formatPhoneWithHyphens(clientDetails.phone1);
                }
                if (clientDetails.resident && !this.prescriptionData.residentNumber) {
                    this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(clientDetails.resident);
                }

                // 산소치료 관련 정보
                if (clientDetails.oxygen_start) {
                    this.prescriptionData.oxygenSdate = new Date(clientDetails.oxygen_start);
                }
                if (clientDetails.oxygen_end) {
                    this.prescriptionData.oxygenEdate = new Date(clientDetails.oxygen_end);
                }

                // 대여 기간 정보
                if (clientDetails.rental_start) {
                    this.prescriptionData.useHomeStartDate = new Date(clientDetails.rental_start);
                    this.prescriptionData.useMobileStartDate = new Date(clientDetails.rental_start);
                }
                if (clientDetails.rental_end) {
                    this.prescriptionData.useHomeEndDate = new Date(clientDetails.rental_end);
                    this.prescriptionData.useMobileEndDate = new Date(clientDetails.rental_end);
                }

                // oxygen_type에 따른 접수구분 설정 및 필드 활성화
                this.setOxygenTypeConfiguration(clientDetails.oxygen_type);

                // 품목 정보에서 산소번호 추출
                if (productInfo.products && productInfo.products.length > 0) {
                    this.extractOxygenNumbersFromProducts(productInfo.products);
                }
            }
        },

        // oxygen_type에 따른 설정
        setOxygenTypeConfiguration(oxygenType) {
            switch (oxygenType) {
                case '가정용':
                    this.prescriptionData.receiptType = '가정용';
                    this.isHomeOxygenEnabled = true;
                    this.isMobileOxygenEnabled = false;
                    break;

                case '휴대용':
                    this.prescriptionData.receiptType = '휴대용';
                    this.isHomeOxygenEnabled = false;
                    this.isMobileOxygenEnabled = true;
                    break;

                case '동시접수':
                    this.prescriptionData.receiptType = '동시접수';
                    this.isHomeOxygenEnabled = true;
                    this.isMobileOxygenEnabled = true;
                    break;

                default:
                    this.prescriptionData.receiptType = '';
                    this.isHomeOxygenEnabled = true;
                    this.isMobileOxygenEnabled = true;
            }
        },

        // 품목에서 산소번호 추출
        extractOxygenNumbersFromProducts(products) {
            products.forEach((product, index) => {
                const productName = product.name || product.name0 || product.name2 || '';
                const productName1 = product.name1 || '';
                const productName2 = product.name2 || '';
                const productName3 = product.name3 || '';
                const productName4 = product.name4 || '';

                // 가정용 산소발생기 확인
                if (productName2 === '가정용' && productName1 === '산소발생기') {
                    // 바코드에서 산소번호 추출
                    if (product.barcodes && product.barcodes.length > 0) {
                        this.prescriptionData.oxygenHomeGenerator = product.barcodes[0];
                    }
                    // 또는 제품명에서 추출
                    else if (productName3) {
                        this.prescriptionData.oxygenHomeGenerator = productName3;
                    }
                }

                // 휴대용 산소발생기 확인
                if (productName2 === '휴대용' && productName1 === '산소발생기') {
                    // 바코드에서 산소번호 추출
                    if (product.barcodes && product.barcodes.length > 0) {
                        this.prescriptionData.oxygenMobileGenerator = product.barcodes[0];
                    }
                    // 또는 제품명에서 추출
                    else if (productName3) {
                        this.prescriptionData.oxygenMobileGenerator = productName3;
                    }
                }
            });
        },

        async loadClaimHistory() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/oxygenClaimData/${this.orderId}`);
                if (response.data && response.data.success) {

                    this.claimHistory = response.data.data.map((claim, index) => {
                        // 청구 차수 결정
                        const homeCount = claim.home_count ? parseInt(claim.home_count.replace('차', '')) : 0;
                        const mobileCount = claim.mobile_count ? parseInt(claim.mobile_count.replace('차', '')) : 0;
                        const maxCount = Math.max(homeCount, mobileCount);
                        const billingCount = maxCount > 0 ? `${maxCount}차` : '1차';

                        // 실제 DB의 status 값을 사용
                        const currentStep = this.getCurrentStepFromDB(claim);

                        // 전자세금계산서 발행 여부 확인
                        const taxInvoiceIssued = claim.tax_invoice && claim.tax_invoice.trim() !== '' ? '발행완료' : '미발행';

                        // 현금영수증 발행 여부 확인 (cash_receipt로 확인)
                        const cashReceiptIssued = claim.cash_receipt && claim.cash_receipt.trim() !== '' ? '발행완료' : '미발행';

                        return {
                            id: claim.num,
                            title: `${billingCount}`,
                            status: currentStep.status,
                            statusText: currentStep.text,
                            billingCount: billingCount,
                            prescriptionPeriod: this.formatDateRange(claim.start_date, claim.end_date),
                            createdDate: this.formatDate(claim.created_at),
                            // 문서 발행 여부 직접 추가
                            taxInvoiceIssued: taxInvoiceIssued,
                            cashReceiptIssued: cashReceiptIssued,
                            documentIssuance: {
                                taxInvoice: {
                                    issued: claim.tax_invoice && claim.tax_invoice.trim() !== '',
                                    confirmNum: claim.tax_invoice || '', // 승인번호
                                    statusText: taxInvoiceIssued
                                },
                                cashReceipt: {
                                    issued: claim.cash_receipt && claim.cash_receipt.trim() !== '',
                                    confirmNum: claim.cash_receipt || '', // 승인번호
                                    statusText: cashReceiptIssued
                                }
                            },
                            data: {
                                prescriptionData: {
                                    certificateName: claim.certificate_id || '',
                                    cert_password: claim.certificate_pw || '',
                                    name: claim.name || '',
                                    residentNumber: this.formatResidentNumberWithHyphens(claim.number || ''),
                                    phone: this.formatPhoneWithHyphens(claim.phone || ''),
                                    diseaseName: claim.disease_name || '',
                                    diseaseCode: claim.disease_code || '',
                                    doctorLicense: claim.doctor_number || '',
                                    specialistLicense: claim.specialist_number || '',
                                    oxygenSdate: claim.start_date ? new Date(claim.start_date) : null,
                                    oxygenEdate: claim.end_date ? new Date(claim.end_date) : null,
                                    oxygenDate: claim.oxygen_date ? new Date(claim.oxygen_date) : null,
                                    receiptType: claim.receipt_type || '',
                                    issuanceType: claim.issuance_type || '',
                                    useHomeStartDate: claim.home_start_date ? new Date(claim.home_start_date) : null,
                                    useHomeEndDate: claim.home_end_date ? new Date(claim.home_end_date) : null,
                                    useMobileStartDate: claim.mobile_start_date ? new Date(claim.mobile_start_date) : null,
                                    useMobileEndDate: claim.mobile_end_date ? new Date(claim.mobile_end_date) : null,
                                    oxygenHomeGenerator: claim.home_oxygen_number || '',
                                    oxygenMobileGenerator: claim.mobile_oxygen_number || '',
                                    homeBillingCount: claim.home_count || '',
                                    mobileBillingCount: claim.mobile_count || '',
                                    taxInvoice: claim.tax_invoice || '',
                                    taxInvoiceDate: claim.tax_invoice_date ? new Date(claim.tax_invoice_date) : null,
                                    taxInvoiceAmount: claim.tax_invoice_amount || '',
                                    cashReceipt: claim.cash_receipt || '',
                                    cashReceiptDate: claim.cash_receipt_date ? new Date(claim.cash_receipt_date) : null,
                                    cashReceiptAmount: claim.cash_receipt_amount || '',
                                    bloodGasItem1: claim.gas_item1 === 1,
                                    bloodGasItem2: claim.gas_item2 === 1,
                                    bloodGasItem3: claim.gas_item3 === 1,
                                    bloodGasItem4: claim.gas_item4 === 1,
                                    bloodGasItem5: claim.gas_item5 === 1,
                                    bloodGasItem6: claim.gas_item6 === 1,
                                    bloodGasItem7: claim.gas_item7 === 1,
                                    oxygenSaturationItem1: claim.oxygen_item1 === 1,
                                    oxygenSaturationItem2: claim.oxygen_item2 === 1,
                                    oxygenSaturationItem3: claim.oxygen_item3 === 1,
                                    oxygenSaturationItem4: claim.oxygen_item4 === 1,
                                    oxygenSaturationItem5: claim.oxygen_item5 === 1,
                                    status: claim.status
                                }
                            }
                        };
                    });

                    // 첫 번째 청구가 있으면 자동으로 로드 (테스트용)
                    if (this.claimHistory.length > 0) {
                        this.selectClaim(this.claimHistory[0], 0);
                    }
                }
            } catch (error) {
                console.error('청구 이력 로드 오류:', error);
            }
        },

        // 새로운 메서드 추가: DB의 status 값을 기반으로 현재 단계 결정
        getCurrentStepFromDB(claim) {
            // DB에서 가져온 실제 status 값 사용
            const dbStatus = claim.status;

            switch (dbStatus) {
                case '청구정보저장':
                    return { status: 'info', text: '청구정보저장' };
                case '환자등록':
                    return { status: 'warning', text: '환자등록' };
                case '위임내역등록':
                    return { status: 'warning', text: '위임내역등록' };
                case '청구등록':
                    return { status: 'success', text: '청구등록' };
                default:
                    return { status: 'info', text: dbStatus };
            }
        },

        // loadClaimData 메서드 수정
        loadClaimData(claim) {
            if (!claim.data || !claim.data.prescriptionData) {
                console.error('청구 데이터가 없습니다:', claim);
                return;
            }

            const prescriptionData = claim.data.prescriptionData;

            // Vue의 반응성을 위해 $nextTick 사용
            this.$nextTick(() => {
                this.prescriptionData.bloodGasItem1 = Boolean(prescriptionData.bloodGasItem1);
                this.prescriptionData.bloodGasItem2 = Boolean(prescriptionData.bloodGasItem2);
                this.prescriptionData.bloodGasItem3 = Boolean(prescriptionData.bloodGasItem3);
                this.prescriptionData.bloodGasItem4 = Boolean(prescriptionData.bloodGasItem4);
                this.prescriptionData.bloodGasItem5 = Boolean(prescriptionData.bloodGasItem5);
                this.prescriptionData.bloodGasItem6 = Boolean(prescriptionData.bloodGasItem6);
                this.prescriptionData.bloodGasItem7 = Boolean(prescriptionData.bloodGasItem7);
                this.prescriptionData.oxygenSaturationItem1 = Boolean(prescriptionData.oxygenSaturationItem1);
                this.prescriptionData.oxygenSaturationItem2 = Boolean(prescriptionData.oxygenSaturationItem2);
                this.prescriptionData.oxygenSaturationItem3 = Boolean(prescriptionData.oxygenSaturationItem3);
                this.prescriptionData.oxygenSaturationItem4 = Boolean(prescriptionData.oxygenSaturationItem4);
                this.prescriptionData.oxygenSaturationItem5 = Boolean(prescriptionData.oxygenSaturationItem5);

                // 나머지 필드들도 설정
                this.prescriptionData.certificateName = prescriptionData.certificateName || '';
                this.prescriptionData.cert_password = prescriptionData.cert_password || '';
                this.prescriptionData.name = prescriptionData.name || '';
                this.prescriptionData.residentNumber = prescriptionData.residentNumber || '';
                this.prescriptionData.phone = prescriptionData.phone || '';
                this.prescriptionData.diseaseName = prescriptionData.diseaseName || '';
                this.prescriptionData.diseaseCode = prescriptionData.diseaseCode || '';
                this.prescriptionData.doctorLicense = prescriptionData.doctorLicense || '';
                this.prescriptionData.specialistLicense = prescriptionData.specialistLicense || '';
                this.prescriptionData.oxygenSdate = prescriptionData.oxygenSdate || null;
                this.prescriptionData.oxygenEdate = prescriptionData.oxygenEdate || null;
                this.prescriptionData.oxygenDate = prescriptionData.oxygenDate || null;
                this.prescriptionData.receiptType = prescriptionData.receiptType || '';
                this.prescriptionData.issuanceType = prescriptionData.issuanceType || '';
                this.prescriptionData.useHomeStartDate = prescriptionData.useHomeStartDate || null;
                this.prescriptionData.useHomeEndDate = prescriptionData.useHomeEndDate || null;
                this.prescriptionData.useMobileStartDate = prescriptionData.useMobileStartDate || null;
                this.prescriptionData.useMobileEndDate = prescriptionData.useMobileEndDate || null;
                this.prescriptionData.oxygenHomeGenerator = prescriptionData.oxygenHomeGenerator || '';
                this.prescriptionData.oxygenMobileGenerator = prescriptionData.oxygenMobileGenerator || '';
                this.prescriptionData.homeBillingCount = prescriptionData.homeBillingCount || '';
                this.prescriptionData.mobileBillingCount = prescriptionData.mobileBillingCount || '';
                this.prescriptionData.cashReceipt = prescriptionData.cashReceipt || '';
                this.prescriptionData.cashReceiptDate = prescriptionData.cashReceiptDate || null;
                this.prescriptionData.cashReceiptAmount = prescriptionData.cashReceiptAmount || '';
                this.prescriptionData.taxInvoice = prescriptionData.taxInvoice || '';
                this.prescriptionData.taxInvoiceDate = prescriptionData.taxInvoiceDate || null;
                this.prescriptionData.taxInvoiceAmount = prescriptionData.taxInvoiceAmount || '';

                // status 값 복사
                this.prescriptionData.status = prescriptionData.status || claim.statusText;

                // oxygen_type에 따른 필드 활성화 설정
                this.setOxygenTypeConfiguration(prescriptionData.receiptType);

                // 강제로 UI 업데이트 트리거
                this.$forceUpdate();
            });
        },

        // 3. saveClaimInfo 메서드에서 필드명 수정 (DB 컬럼명과 일치)
        saveClaimInfo() {
            const formatDateForAPI = (date) => {
                if (!date) return null;
                if (date instanceof Date) {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
                return date;
            };

            const saveData = {
                orderId: this.orderId,
                companykey: this.$globalState.company_key,
                certificateName: this.prescriptionData.certificateName,
                certPassword: this.prescriptionData.cert_password,
                name: this.prescriptionData.name,
                residentNumber: this.removeHyphens(this.prescriptionData.residentNumber),
                phone: this.removeHyphens(this.prescriptionData.phone),
                oxygenDate: formatDateForAPI(this.prescriptionData.oxygenDate),
                diseaseName: this.prescriptionData.diseaseName,
                diseaseCode: this.prescriptionData.diseaseCode,
                doctorLicense: this.prescriptionData.doctorLicense,
                specialistLicense: this.prescriptionData.specialistLicense,
                startDate: formatDateForAPI(this.prescriptionData.oxygenSdate),
                endDate: formatDateForAPI(this.prescriptionData.oxygenEdate),
                receiptType: this.prescriptionData.receiptType,
                issuanceType: this.prescriptionData.issuanceType,
                homeStartDate: formatDateForAPI(this.prescriptionData.useHomeStartDate),
                homeEndDate: formatDateForAPI(this.prescriptionData.useHomeEndDate),
                mobileStartDate: formatDateForAPI(this.prescriptionData.useMobileStartDate),
                mobileEndDate: formatDateForAPI(this.prescriptionData.useMobileEndDate),
                homeOxygenNumber: this.prescriptionData.oxygenHomeGenerator,
                mobileOxygenNumber: this.prescriptionData.oxygenMobileGenerator,
                homeCount: this.prescriptionData.homeBillingCount,
                mobileCount: this.prescriptionData.mobileBillingCount,
                cashReceipt: this.prescriptionData.cashReceipt,
                cashReceiptDate: formatDateForAPI(this.prescriptionData.cashReceiptDate),
                cashReceiptAmount: this.prescriptionData.cashReceiptAmount,
                taxInvoice: this.prescriptionData.taxInvoice,
                taxInvoiceDate: formatDateForAPI(this.prescriptionData.taxInvoiceDate),
                taxInvoiceAmount: this.prescriptionData.taxInvoiceAmount,
                gasItem1: this.prescriptionData.bloodGasItem1 ? 1 : 0,
                gasItem2: this.prescriptionData.bloodGasItem2 ? 1 : 0,
                gasItem3: this.prescriptionData.bloodGasItem3 ? 1 : 0,
                gasItem4: this.prescriptionData.bloodGasItem4 ? 1 : 0,
                gasItem5: this.prescriptionData.bloodGasItem5 ? 1 : 0,
                gasItem6: this.prescriptionData.bloodGasItem6 ? 1 : 0,
                gasItem7: this.prescriptionData.bloodGasItem7 ? 1 : 0,
                oxygenItem1: this.prescriptionData.oxygenSaturationItem1 ? 1 : 0,
                oxygenItem2: this.prescriptionData.oxygenSaturationItem2 ? 1 : 0,
                oxygenItem3: this.prescriptionData.oxygenSaturationItem3 ? 1 : 0,
                oxygenItem4: this.prescriptionData.oxygenSaturationItem4 ? 1 : 0,
                oxygenItem5: this.prescriptionData.oxygenSaturationItem5 ? 1 : 0,
                // 처음 등록할 때는 "청구정보저장"으로 설정, 수정할 때는 기존 status 유지
                status: this.selectedClaimIndex >= 0
                    ? (this.prescriptionData.status || '청구정보저장')
                    : (this.prescriptionData.status || '청구정보저장')
            };

            if (!saveData.companykey) {
                this.$swal.fire({
                    icon: 'error',
                    title: '회사 정보 없음',
                    text: '회사 정보를 찾을 수 없습니다. 다시 로그인해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            console.log('저장할 saveData.status:', saveData.status);
            this.saveClaimDataToServer(saveData);
        },

        // 선택된 청구 삭제
        async deleteSelectedClaim() {
            if (this.selectedClaimIndex < 0) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '청구를 선택해주세요',
                    text: '삭제할 청구 이력을 먼저 선택해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            const selectedClaim = this.claimHistory[this.selectedClaimIndex];

            const result = await this.$swal.fire({
                title: '청구 삭제',
                text: `"${selectedClaim.title}" 청구를 삭제하시겠습니까?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: '삭제',
                cancelButtonText: '취소',
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280'
            });

            if (result.isConfirmed) {
                try {
                    // API 호출로 청구 삭제
                    await this.$axios.delete(`${this.$apiUrls.hanqmedi}/oxygenClaimData/${selectedClaim.id}`);

                    // 청구 이력에서 제거
                    this.claimHistory.splice(this.selectedClaimIndex, 1);

                    // 선택 해제 및 폼 초기화
                    this.selectedClaimIndex = -1;
                    this.resetForm();

                    this.$swal.fire({
                        icon: 'success',
                        title: '삭제 완료',
                        text: '선택한 청구가 성공적으로 삭제되었습니다.',
                        confirmButtonText: '확인'
                    });

                    // 산소발생기 환자 목록 새로고침
                    window.dispatchEvent(new CustomEvent('refresh-oxygen-patients'));
                } catch (error) {
                    console.error('청구 삭제 오류:', error);
                    this.$swal.fire({
                        icon: 'error',
                        title: '삭제 실패',
                        text: '삭제 중 오류가 발생했습니다.',
                        confirmButtonText: '확인'
                    });
                }
            }
        },

        // 청구 항목 선택
        selectClaim(claim, index) {
            // 이미 선택된 항목을 다시 클릭하면 선택 해제
            if (this.selectedClaimIndex === index) {
                this.selectedClaimIndex = -1;
                this.resetForm();
                return;
            }

            this.selectedClaimIndex = index;
            // 선택된 청구 데이터를 폼에 로드
            this.loadClaimData(claim);
        },

        // 파일 드래그 앤 드롭 핸들러
        handlePrescriptionDragEnter(e) {
            e.preventDefault();
            this.isPrescriptionDragging = true;
        },

        handlePrescriptionDragOver(e) {
            e.preventDefault();
        },

        handlePrescriptionDragLeave(e) {
            e.preventDefault();
            this.isPrescriptionDragging = false;
        },

        handlePrescriptionDrop(e) {
            e.preventDefault();
            this.isPrescriptionDragging = false;
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                this.handlePrescriptionFile(files[0]);
            }
        },

        handlePrescriptionPaste(e) {
            const items = e.clipboardData.items;
            for (let item of items) {
                if (item.type.indexOf('image') !== -1) {
                    const file = item.getAsFile();
                    this.handlePrescriptionFile(file);
                    break;
                }
            }
        },

        triggerPrescriptionFileInput() {
            if (this.$refs.prescriptionFileInput) {
                this.$refs.prescriptionFileInput.click();
            }
        },

        handlePrescriptionFileChange(e) {
            const file = e.target.files[0];
            if (file) {
                this.handlePrescriptionFile(file);
            }
        },

        removePrescriptionFile() {
            this.prescriptionFile = null;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // ========================================================================
        // OCR 분석 관련 함수
        // ========================================================================
        // OCR 분석 시작
        async analyzePrescription() {
            if (!this.prescriptionFile) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '파일 없음',
                    text: '처방전 파일을 먼저 업로드해주세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.isOcrAnalyzing = true;

            try {
                // 파일 타입에 따른 처리
                let fileToSend = this.prescriptionFile;

                // PDF 파일인 경우 크기 체크
                if (this.prescriptionFile.type === 'application/pdf') {
                    if (this.prescriptionFile.size > 30 * 1024 * 1024) { // 30MB 제한
                        this.$swal.fire({
                            icon: 'error',
                            title: '파일 크기 초과',
                            text: 'PDF 파일 크기가 30MB를 초과합니다. 더 작은 파일을 업로드해주세요.',
                            confirmButtonText: '확인'
                        });
                        return;
                    }
                } else {
                    // 이미지 파일인 경우 압축 처리
                    if (this.prescriptionFile.size > 10 * 1024 * 1024) { // 10MB 초과시 압축
                        try {
                            fileToSend = await this.compressImage(this.prescriptionFile);
                        } catch (compressError) {
                            fileToSend = this.prescriptionFile;
                        }
                    }
                }

                // FormData 생성
                const formData = new FormData();

                // 압축된 이미지인 경우 Blob으로 변환
                if (typeof fileToSend === 'string' && fileToSend.startsWith('data:')) {
                    // base64 데이터를 Blob으로 변환
                    const response = await fetch(fileToSend);
                    const blob = await response.blob();
                    formData.append('file', blob, this.prescriptionFile.name);
                } else {
                    formData.append('file', fileToSend);
                }

                // 청구 유형 추가
                formData.append('claimType', this.claimType);

                // API URL 설정
                let apiUrl;
                try {
                    const config = this.$getEnvironmentConfig ? this.$getEnvironmentConfig() : null;
                    apiUrl = config ? `${config.hanqmedi}/processOcr` : `${this.$apiUrls.hanqmedi}/processOcr`;
                } catch (error) {
                    apiUrl = `${this.$apiUrls.hanqmedi}/processOcr`;
                }

                // fetch API를 사용하여 FormData 전송
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const responseData = await response.json();
                console.log('OCR 응답:', responseData);

                if (responseData.success) {
                    this.processOcrResult(responseData.data);
                    this.$swal.fire({
                        icon: 'success',
                        title: '분석 완료',
                        text: '처방전 데이터가 성공적으로 분석되었습니다.',
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(responseData.message || 'OCR 분석에 실패했습니다.');
                }
            } catch (error) {
                console.error('OCR 분석 오류:', error);

                let errorMessage = 'OCR 분석 중 오류가 발생했습니다.';
                if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '분석 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isOcrAnalyzing = false;
            }
        },

        // 이미지 압축 함수
        async compressImage(file) {
            return new Promise((resolve) => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    const maxWidth = 1920;
                    const maxHeight = 1080;
                    let { width, height } = img;

                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob(resolve, 'image/jpeg', 0.8);
                };

                img.src = URL.createObjectURL(file);
            });
        },

        // OCR 결과 처리
        processOcrResult(ocrData) {
            if (!ocrData || !ocrData.length) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '분석 결과 없음',
                    text: '처방전에서 정보를 추출할 수 없습니다.',
                    confirmButtonText: '확인'
                });
                return;
            }

            // OCR 데이터 배열을 저장 (필드별 추출에 사용)
            this.ocrDataArray = ocrData;

            // 첫 번째 결과 사용 (기존 호환성 유지)
            const result = ocrData[0];
            this.processSingleOcrResult(result);
        },

        // 단일 OCR 결과 처리
        processSingleOcrResult(result) {
            try {
                // result가 객체인 경우 inferText 추출
                const inferText = result.inferText || '';

                // OCR 데이터 배열에서 각 필드 찾기
                // 전체 OCR 데이터 배열을 사용하여 각 필드별로 추출
                if (this.ocrDataArray && Array.isArray(this.ocrDataArray)) {
                    // 1. 성명 필드 찾기 - 여러 필드에서 시도
                    const nameFields = this.ocrDataArray.filter(field =>
                        field.name === '성명' ||
                        field.name === '수진자 정보' ||
                        field.name === '수진자'
                    );
                    for (const field of nameFields) {
                        if (field && field.inferText) {
                            const extractedName = this.extractName(field.inferText);
                            if (extractedName && extractedName.length >= 2 && extractedName.length <= 4) {
                                this.prescriptionData.name = extractedName;
                                break;
                            }
                        }
                    }

                    // 2. 주민등록번호 필드 찾기 - 여러 필드에서 시도
                    const residentFields = this.ocrDataArray.filter(field =>
                        field.name === '주민등록번호' ||
                        field.name === '수진자 정보' ||
                        field.name === '상병 정보'
                    );
                    for (const field of residentFields) {
                        if (field && field.inferText) {
                            const extractedResident = this.extractResidentNumber(field.inferText);
                            if (extractedResident && extractedResident.length === 13) {
                                this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(extractedResident);
                                break;
                            }
                        }
                    }

                    // 3. 전화번호 필드 찾기 - 휴대전화 우선, 여러 필드에서 시도
                    // 먼저 휴대전화 찾기
                    const mobilePhoneFields = this.ocrDataArray.filter(field =>
                        field.inferText && (
                            field.inferText.includes('휴대전화') ||
                            field.inferText.includes('010') ||
                            field.inferText.includes('011') ||
                            field.inferText.includes('016') ||
                            field.inferText.includes('017') ||
                            field.inferText.includes('018') ||
                            field.inferText.includes('019')
                        )
                    );

                    for (const field of mobilePhoneFields) {
                        if (field && field.inferText) {
                            const extractedPhone = this.extractPhone(field.inferText);
                            if (extractedPhone && (extractedPhone.length === 10 || extractedPhone.length === 11)) {
                                this.prescriptionData.phone = this.formatPhoneWithHyphens(extractedPhone);
                                break;
                            }
                        }
                    }

                    // 휴대전화를 찾지 못한 경우 일반 전화번호 필드에서 찾기
                    if (!this.prescriptionData.phone) {
                        const phoneFields = this.ocrDataArray.filter(field =>
                            field.name === '전화번호' ||
                            field.name === '상병 정보' ||
                            field.name === '수진자 정보'
                        );
                        for (const field of phoneFields) {
                            if (field && field.inferText) {
                                const extractedPhone = this.extractPhone(field.inferText);
                                if (extractedPhone && (extractedPhone.length === 10 || extractedPhone.length === 11)) {
                                    this.prescriptionData.phone = this.formatPhoneWithHyphens(extractedPhone);
                                    break;
                                }
                            }
                        }
                    }

                    // 4. 상병명 필드 찾기 - 긴 텍스트 처리
                    const diseaseNameField = this.ocrDataArray.find(field =>
                        field.name === '상병명' ||
                        field.name === '상병 정보'
                    );
                    if (diseaseNameField && diseaseNameField.inferText) {
                        const extractedDiseaseName = this.extractDiseaseName(diseaseNameField.inferText);
                        if (extractedDiseaseName && extractedDiseaseName.length > 0) {
                            this.prescriptionData.diseaseName = extractedDiseaseName;
                        }
                    }

                    // 5. 상병코드 필드 찾기 - 영문+숫자+점 패턴
                    const diseaseCodeField = this.ocrDataArray.find(field =>
                        field.name === '상병코드' ||
                        field.name === '상병 정보'
                    );
                    if (diseaseCodeField && diseaseCodeField.inferText) {
                        const extractedDiseaseCode = this.extractDiseaseCode(diseaseCodeField.inferText);
                        if (extractedDiseaseCode) {
                            this.prescriptionData.diseaseCode = extractedDiseaseCode;
                        }
                    }

                    // 6. 의사면허번호 필드 찾기 - "제", "호" 제거
                    const doctorLicenseFields = this.ocrDataArray.filter(field =>
                        field.name === '의사면허번호' ||
                        field.name === '의사 정보' ||
                        field.name === '면허번호'
                    );
                    for (const field of doctorLicenseFields) {
                        if (field && field.inferText) {
                            const extractedDoctorLicense = this.extractDoctorLicense(field.inferText);
                            if (extractedDoctorLicense && extractedDoctorLicense.length >= 4) {
                                this.prescriptionData.doctorLicense = extractedDoctorLicense;
                                break;
                            }
                        }
                    }

                    // 7. 전문의번호 필드 찾기 - 4자리 숫자
                    const specialistLicenseFields = this.ocrDataArray.filter(field =>
                        field.name === '전문의번호' ||
                        field.name === '의사 정보' ||
                        field.name === '자격번호'
                    );
                    for (const field of specialistLicenseFields) {
                        if (field && field.inferText) {
                            const extractedSpecialistLicense = this.extractSpecialistLicense(field.inferText);
                            if (extractedSpecialistLicense && extractedSpecialistLicense.length >= 4) {
                                this.prescriptionData.specialistLicense = extractedSpecialistLicense;
                                break;
                            }
                        }
                    }

                    // 8. 처방일자 필드 찾기 - "2024년 02월 21일" 형식
                    const prescriptionDateFields = this.ocrDataArray.filter(field =>
                        field.name === '처방일자' ||
                        field.name === '처방날짜 정보' ||
                        field.name === '발급일'
                    );
                    for (const field of prescriptionDateFields) {
                        if (field && field.inferText) {
                            const extractedDate = this.extractOxygenDate(field.inferText);
                            if (extractedDate) {
                                this.prescriptionData.oxygenDate = new Date(extractedDate);
                                break;
                            }
                        }
                    }

                    // 9. 산소치료 처방기간 필드 찾기 - "2024-02-21 ~ 2025-02-20 까지" 형식
                    const prescriptionPeriodFields = this.ocrDataArray.filter(field =>
                        field.name === '산소치료 처방기간' ||
                        field.name === '처방날짜 정보' ||
                        field.name === '처방기간'
                    );
                    for (const field of prescriptionPeriodFields) {
                        if (field && field.inferText) {
                            const startDate = this.extractOxygenStartDate(field.inferText);
                            const endDate = this.extractOxygenEndDate(field.inferText);
                            if (startDate) {
                                this.prescriptionData.oxygenSdate = new Date(startDate);
                            }
                            if (endDate) {
                                this.prescriptionData.oxygenEdate = new Date(endDate);
                            }
                            // 둘 다 찾았으면 중단
                            if (startDate && endDate) {
                                break;
                            }
                        }
                    }

                    // 10. 동맥혈 가스검사 결과 체크박스 처리 - □ 유무로 체크 여부 판단
                    const bloodGasField1 = this.ocrDataArray.find(field => field.name === '동맥혈 가스검사 결과1');
                    if (bloodGasField1 && bloodGasField1.inferText) {
                        const text = bloodGasField1.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 산소분압 55mmHg 이하
                                if (line.match(/55\s*mmHg|55\s*이하|산소분압.*55/)) {
                                    this.prescriptionData.bloodGasItem1 = true;
                                }
                                // 산소포화도 88% 이하
                                if (line.match(/88\s*%|88\s*이하|산소포화도.*88/)) {
                                    this.prescriptionData.bloodGasItem2 = true;
                                }
                            }
                        }
                    }

                    const bloodGasField2 = this.ocrDataArray.find(field => field.name === '동맥혈 가스검사 결과2');
                    if (bloodGasField2 && bloodGasField2.inferText) {
                        const text = bloodGasField2.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 산소분압 56~59mmHg
                                if (line.match(/56.*59|56-59|56\s*~\s*59|56\s*~\s*59\s*mmHg/)) {
                                    this.prescriptionData.bloodGasItem3 = true;
                                }
                                // 산소포화도 89% 이상
                                if (line.match(/89\s*%|89\s*이상|산소포화도.*89/)) {
                                    this.prescriptionData.bloodGasItem4 = true;
                                }
                            }
                        }
                    }

                    const bloodGasField3 = this.ocrDataArray.find(field => field.name === '동맥혈 가스검사 결과3');
                    if (bloodGasField3 && bloodGasField3.inferText) {
                        const text = bloodGasField3.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 적혈구 증가증
                                if (line.match(/적혈구|헤마토크리트|55\s*%/)) {
                                    this.prescriptionData.bloodGasItem5 = true;
                                }
                                // 울혈성 심부전
                                if (line.match(/심부전|말초부종/)) {
                                    this.prescriptionData.bloodGasItem6 = true;
                                }
                                // 폐동맥고혈압
                                if (line.match(/폐동맥고혈압/)) {
                                    this.prescriptionData.bloodGasItem7 = true;
                                }
                            }
                        }
                    }

                    // 11. 산소포화도 검사결과 체크박스 처리 - □ 유무로 체크 여부 판단
                    const oxygenSaturationField1 = this.ocrDataArray.find(field => field.name === '산소포화도 검사결과1');
                    if (oxygenSaturationField1 && oxygenSaturationField1.inferText) {
                        const text = oxygenSaturationField1.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 산소포화도 88% 이하
                                if (line.match(/88\s*%\s*이하|산소포화도.*88.*이하|88\s*%/)) {
                                    this.prescriptionData.oxygenSaturationItem1 = true;
                                }
                            }
                        }
                    }

                    const oxygenSaturationField2 = this.ocrDataArray.find(field => field.name === '산소포화도 검사결과2');
                    if (oxygenSaturationField2 && oxygenSaturationField2.inferText) {
                        const text = oxygenSaturationField2.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 산소포화도 89% 이상
                                if (line.match(/89\s*%\s*이상|산소포화도.*89.*이상|89\s*%/)) {
                                    this.prescriptionData.oxygenSaturationItem2 = true;
                                }
                            }
                        }
                    }

                    const oxygenSaturationField3 = this.ocrDataArray.find(field => field.name === '산소포화도 검사결과3');
                    if (oxygenSaturationField3 && oxygenSaturationField3.inferText) {
                        const text = oxygenSaturationField3.inferText;
                        const lines = text.split('\n');

                        for (const line of lines) {
                            // □가 없는 경우에만 체크
                            if (!line.includes('□') && !line.includes('☐')) {
                                // 적혈구 증가증
                                if (line.match(/적혈구|헤마토크리트|55\s*%/)) {
                                    this.prescriptionData.oxygenSaturationItem3 = true;
                                }
                                // 울혈성 심부전
                                if (line.match(/심부전|말초부종/)) {
                                    this.prescriptionData.oxygenSaturationItem4 = true;
                                }
                                // 폐동맥고혈압
                                if (line.match(/폐동맥고혈압/)) {
                                    this.prescriptionData.oxygenSaturationItem5 = true;
                                }
                            }
                        }
                    }
                }

                this.$swal.fire({
                    icon: 'success',
                    title: '분석 완료',
                    text: '처방전 정보가 자동으로 입력되었습니다.',
                    confirmButtonText: '확인'
                });

            } catch (error) {
                console.error('OCR 결과 처리 오류:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '처리 실패',
                    text: 'OCR 결과 처리 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 숫자만 추출하는 헬퍼 함수
        extractNumbersOnly(text) {
            if (!text || typeof text !== 'string') return '';
            return text.replace(/[^0-9]/g, '');
        },

        // 성명 추출 개선
        extractName(text) {
            // "성명\n지수자" 패턴
            const nameMatch = text.match(/성명\s*\n\s*([가-힣]{2,4})/);
            if (nameMatch) return nameMatch[1];

            // "성명 OOO" 패턴
            const nameMatch2 = text.match(/성명\s*([가-힣]{2,4})/);
            if (nameMatch2) return nameMatch2[1];

            // "수진자 성명 OOO" 패턴
            const patientNameMatch = text.match(/수진자\s*성명\s*([가-힣]{2,4})/);
            if (patientNameMatch) return patientNameMatch[1];

            // "수진자 OOO" 패턴
            const patientMatch = text.match(/수진자\s+([가-힣]{2,4})(?=\s|$)/);
            if (patientMatch) return patientMatch[1];

            // "환자명 OOO" 패턴
            const patientNameMatch2 = text.match(/환자명\s*([가-힣]{2,4})/);
            if (patientNameMatch2) return patientNameMatch2[1];

            // 한글 이름만 추출 (2-4자)
            const koreanNameMatch = text.match(/([가-힣]{2,4})/);
            if (koreanNameMatch) return koreanNameMatch[1];

            return null;
        },

        // 주민등록번호 추출 개선 - 숫자만 추출
        extractResidentNumber(text) {
            if (!text || typeof text !== 'string') return null;

            // 먼저 숫자만 추출
            const numbers = this.extractNumbersOnly(text);

            // 13자리 숫자인 경우
            if (numbers.length === 13) {
                return numbers;
            }

            // 12자리 이상인 경우 앞 13자리만
            if (numbers.length >= 12) {
                return numbers.substring(0, 13);
            }

            // 기존 패턴 매칭도 시도 (하이픈 포함)
            const match1 = text.match(/주민등록번호[^\n]*\n\s*(\d{6})-?(\d{7})/);
            if (match1) return match1[1] + match1[2];

            const match2 = text.match(/(\d{6})\s+-?\s*(\d{7})/);
            if (match2) return match2[1] + match2[2];

            const match3 = text.match(/(\d{6})-(\d{7})/);
            if (match3) return match3[1] + match3[2];

            const match4 = text.match(/(\d{6})(\d{7})/);
            if (match4) return match4[1] + match4[2];

            return null;
        },

        // 전화번호 추출 개선 - 숫자만 추출
        extractPhone(text) {
            if (!text || typeof text !== 'string') return null;

            // 휴대전화 우선 추출 (010, 011, 016, 017, 018, 019로 시작)
            const mobileMatch = text.match(/(?:휴대전화|010|011|016|017|018|019)[^\d]*(\d{3})-?(\d{4})-?(\d{4})/);
            if (mobileMatch) {
                const fullNumber = mobileMatch[1] + mobileMatch[2] + mobileMatch[3];
                if (fullNumber.length === 10 || fullNumber.length === 11) {
                    return fullNumber;
                }
            }

            // 먼저 숫자만 추출
            const numbers = this.extractNumbersOnly(text);

            // 11자리 숫자이고 010, 011, 016, 017, 018, 019로 시작하는 경우
            if (numbers.length === 11 && /^(010|011|016|017|018|019)/.test(numbers)) {
                return numbers;
            }

            // 10-11자리 숫자인 경우 (전화번호)
            if (numbers.length >= 10 && numbers.length <= 11) {
                return numbers;
            }

            // 기존 패턴 매칭도 시도
            const homeMatch = text.match(/자택[^\d]*(\d{3})-?(\d{3,4})-?(\d{4})/);
            if (homeMatch) {
                const fullNumber = homeMatch[1] + homeMatch[2] + homeMatch[3];
                if (fullNumber.length === 10 || fullNumber.length === 11) {
                    return fullNumber;
                }
            }

            const match1 = text.match(/(\d{3})-?(\d{3,4})-?(\d{4})/);
            if (match1) {
                const fullNumber = match1[1] + match1[2] + match1[3];
                if (fullNumber.length === 10 || fullNumber.length === 11) {
                    return fullNumber;
                }
            }

            const match2 = text.match(/(\d{3})\s+(\d{3,4})\s+(\d{4})/);
            if (match2) {
                const fullNumber = match2[1] + match2[2] + match2[3];
                if (fullNumber.length === 10 || fullNumber.length === 11) {
                    return fullNumber;
                }
            }

            return null;
        },

        // 상병명 추출 개선
        extractDiseaseName(text) {
            if (!text || typeof text !== 'string') return null;

            // 1. "상병명\n급성악화를 동반한만성폐쇄성폐질환" 패턴 (줄바꿈 포함)
            const match1 = text.match(/상병명\s*\n\s*([가-힣\s()A-Za-z0-9.,·]+)/);
            if (match1) {
                const result = match1[1].trim();
                // 너무 짧거나 의미없는 텍스트 제외
                if (result.length >= 2 && !result.match(/^\d+$/)) {
                    return result;
                }
            }

            // 2. "상병명 급성악화를 동반한만성폐쇄성폐질환" 패턴 (공백 포함)
            const match2 = text.match(/상병명\s+([가-힣\s()A-Za-z0-9.,·]+)/);
            if (match2) {
                const result = match2[1].trim();
                if (result.length >= 2 && !result.match(/^\d+$/)) {
                    return result;
                }
            }

            // 3. "질병명\n급성악화를 동반한만성폐쇄성폐질환" 패턴
            const match3 = text.match(/질병명\s*\n\s*([가-힣\s()A-Za-z0-9.,·]+)/);
            if (match3) {
                const result = match3[1].trim();
                if (result.length >= 2 && !result.match(/^\d+$/)) {
                    return result;
                }
            }

            // 4. "질병명 급성악화를 동반한만성폐쇄성폐질환" 패턴
            const match4 = text.match(/질병명\s+([가-힣\s()A-Za-z0-9.,·]+)/);
            if (match4) {
                const result = match4[1].trim();
                if (result.length >= 2 && !result.match(/^\d+$/)) {
                    return result;
                }
            }

            // 5. "상병명:" 또는 "상병명 :" 패턴
            const match5 = text.match(/상병명\s*[:：]\s*([가-힣\s()A-Za-z0-9.,·]+)/);
            if (match5) {
                const result = match5[1].trim();
                if (result.length >= 2 && !result.match(/^\d+$/)) {
                    return result;
                }
            }

            // 6. "상병명" 다음에 오는 한글 텍스트 (다음 줄이나 공백 후)
            const match6 = text.match(/상병명[^\n가-힣]*([가-힣]+(?:\s+[가-힣]+)*)/);
            if (match6) {
                const result = match6[1].trim();
                if (result.length >= 2) {
                    return result;
                }
            }

            // 7. "상병" 키워드가 있고 그 다음에 한글이 오는 경우
            if (text.includes('상병')) {
                const match7 = text.match(/상병[^\n가-힣]*([가-힣]+(?:\s+[가-힣]+)*)/);
                if (match7) {
                    const result = match7[1].trim();
                    // 너무 짧거나 숫자만 있는 경우 제외
                    if (result.length >= 2 && !result.match(/^\d+$/)) {
                        // 상병코드가 포함된 경우 제거 (예: "J44.1" 같은 것)
                        const cleaned = result.replace(/[A-Z]\d+\.?\d*/g, '').trim();
                        if (cleaned.length >= 2) {
                            return cleaned;
                        }
                    }
                }
            }

            // 8. 한글이 2자 이상 연속으로 나오는 경우 (상병명일 가능성)
            // 단, "상병명", "질병명" 같은 키워드가 앞에 있는 경우만
            if (text.includes('상병') || text.includes('질병')) {
                const match8 = text.match(/([가-힣]{2,}(?:\s+[가-힣]+)*)/);
                if (match8) {
                    const result = match8[1].trim();
                    // 너무 짧거나 일반적인 단어 제외
                    const excludeWords = ['상병명', '질병명', '상병코드', '진료과목', '수진자', '성명'];
                    if (result.length >= 3 && !excludeWords.some(word => result.includes(word))) {
                        return result;
                    }
                }
            }

            return null;
        },

        // 상병코드 추출 개선 - 영문+숫자+점 패턴 (예: J44.1)
        extractDiseaseCode(text) {
            if (!text || typeof text !== 'string') return null;

            // "상병코드\nJ44.1" 패턴
            const match1 = text.match(/상병코드\s*\n\s*([A-Z]\d+\.?\d*)/);
            if (match1) return match1[1].trim();

            // "상병코드 J44.1" 패턴
            const match2 = text.match(/상병코드\s*([A-Z]\d+\.?\d*)/);
            if (match2) return match2[1].trim();

            // "질병코드 J44.1" 패턴
            const match3 = text.match(/질병코드\s*([A-Z]\d+\.?\d*)/);
            if (match3) return match3[1].trim();

            // 영문+숫자+점 패턴 직접 찾기 (예: J44.1, I10, E11.9)
            const match4 = text.match(/([A-Z]\d+\.?\d*)/);
            if (match4) return match4[1];

            // 숫자와 점으로 구성된 코드 패턴 (기존 유지)
            const match5 = text.match(/([0-9]+\.?[0-9]*)/);
            if (match5) return match5[1];

            return null;
        },

        // 의사면허번호 추출 개선 - 숫자만 추출
        extractDoctorLicense(text) {
            if (!text || typeof text !== 'string') return null;

            // "면허번호 제 58623 호" 패턴 - "제", "호" 제거
            const match1 = text.match(/면허번호\s*제\s*(\d+)\s*호/);
            if (match1) return match1[1];

            // "제 58623 호" 패턴
            const match2 = text.match(/제\s*(\d+)\s*호/);
            if (match2) return match2[1];

            // "면허번호 58623" 패턴
            const match3 = text.match(/면허번호\s*(\d+)/);
            if (match3) return match3[1];

            // "의사면허번호 58623" 패턴
            const match4 = text.match(/의사면허번호\s*(\d+)/);
            if (match4) return match4[1];

            // "의사번호 58623" 패턴
            const match5 = text.match(/의사번호\s*(\d+)/);
            if (match5) return match5[1];

            // "담당의사" 다음의 숫자 추출
            const match6 = text.match(/담당의사[^\d]*(\d{4,10})/);
            if (match6) return match6[1];

            // "의사" 또는 "면허" 키워드가 있는 경우 숫자만 추출
            if (text.includes('의사') || text.includes('면허') || text.includes('담당의사')) {
                const numbers = this.extractNumbersOnly(text);
                // 면허번호는 보통 4-10자리
                if (numbers.length >= 4 && numbers.length <= 10) {
                    return numbers;
                }
            }

            return null;
        },

        // 전문의 자격번호 추출 개선 - 숫자만 추출
        extractSpecialistLicense(text) {
            if (!text || typeof text !== 'string') return null;

            // "전문의 자격번호 8954" 패턴
            const match1 = text.match(/전문의\s*자격번호\s*(\d+)/);
            if (match1) return match1[1];

            // "자격번호 8954" 패턴
            const match2 = text.match(/자격번호\s*(\d+)/);
            if (match2) return match2[1];

            // "전문의 8954" 패턴
            const match3 = text.match(/전문의\s*(\d+)/);
            if (match3) return match3[1];

            // "전문과목" 다음의 숫자 추출
            const match4 = text.match(/전문과목[^\d]*(\d{4,10})/);
            if (match4) return match4[1];

            // "전문의" 또는 "자격번호" 키워드가 있는 경우 숫자만 추출
            if (text.includes('전문의') || text.includes('자격번호') || text.includes('전문과목')) {
                const numbers = this.extractNumbersOnly(text);
                // 자격번호는 보통 4-10자리
                if (numbers.length >= 4 && numbers.length <= 10) {
                    return numbers;
                }
            }

            return null;
        },

        // 산소치료 처방일자 추출 개선 - "년", "월", "일" 글자 포함
        extractOxygenDate(text) {
            if (!text || typeof text !== 'string') return null;

            // 1. "2024년 02월 21일" 패턴 (공백 포함)
            const match1 = text.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
            if (match1) {
                const year = match1[1];
                const month = match1[2].padStart(2, '0');
                const day = match1[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 2. "2024년02월21일" 패턴 (공백 없음)
            const match2 = text.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
            if (match2) {
                const year = match2[1];
                const month = match2[2].padStart(2, '0');
                const day = match2[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 3. "2024년 2월 21일" 패턴 (한 자리 월)
            const match3 = text.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
            if (match3) {
                const year = match3[1];
                const month = match3[2].padStart(2, '0');
                const day = match3[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 4. "2024년 02월 21일" 패턴 (OCR 오인식: "년"이 "연"으로, "월"이 "월"로, "일"이 "일"로)
            const match4 = text.match(/(\d{4})\s*[년연]\s*(\d{1,2})\s*[월]\s*(\d{1,2})\s*[일]/);
            if (match4) {
                const year = match4[1];
                const month = match4[2].padStart(2, '0');
                const day = match4[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 5. "2024-02-21" 패턴 (하이픈 형식)
            const match5 = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (match5) {
                const year = match5[1];
                const month = match5[2].padStart(2, '0');
                const day = match5[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 6. "2024.02.21" 패턴 (점 형식)
            const match6 = text.match(/(\d{4})\.(\d{1,2})\.(\d{1,2})/);
            if (match6) {
                const year = match6[1];
                const month = match6[2].padStart(2, '0');
                const day = match6[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 7. "2024/02/21" 패턴 (슬래시 형식)
            const match7 = text.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})/);
            if (match7) {
                const year = match7[1];
                const month = match7[2].padStart(2, '0');
                const day = match7[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 8. "처방일자" 또는 "발급일" 키워드가 있는 경우
            if (text.includes('처방일자') || text.includes('발급일') || text.includes('처방일')) {
                // 숫자 4자리-2자리-2자리 패턴 찾기
                const match8 = text.match(/(\d{4})[^\d]*(\d{1,2})[^\d]*(\d{1,2})/);
                if (match8) {
                    const year = match8[1];
                    const month = match8[2].padStart(2, '0');
                    const day = match8[3].padStart(2, '0');
                    // 유효한 날짜 범위 체크
                    if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                        parseInt(day) >= 1 && parseInt(day) <= 31) {
                        return `${year}-${month}-${day}`;
                    }
                }
            }

            return null;
        },

        // 산소치료 처방기간 시작일 추출 개선 - ~ 표시 기준 앞쪽
        extractOxygenStartDate(text) {
            if (!text || typeof text !== 'string') return null;

            // 1. "2024-02-21 ~ 2025-02-20 까지" 패턴 (하이픈, ~ 포함)
            const match1 = text.match(/(\d{4}-\d{1,2}-\d{1,2})\s*[~∼～]\s*(\d{4}-\d{1,2}-\d{1,2})/);
            if (match1) {
                const startDate = match1[1];
                const parts = startDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 2. "2024.02.21 ~ 2025.02.20" 패턴 (점, ~ 포함)
            const match2 = text.match(/(\d{4}\.\d{1,2}\.\d{1,2})\s*[~∼～]\s*(\d{4}\.\d{1,2}\.\d{1,2})/);
            if (match2) {
                const startDate = match2[1].replace(/\./g, '-');
                const parts = startDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 3. "2024년 02월 21일 ~ 2025년 02월 20일" 패턴 (년월일, ~ 포함)
            const match3 = text.match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일\s*[~∼～]/);
            if (match3) {
                const year = match3[1];
                const month = match3[2].padStart(2, '0');
                const day = match3[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 4. "2024-02-21부터" 또는 "2024-02-21 부터" 패턴
            const match4 = text.match(/(\d{4}-\d{1,2}-\d{1,2})\s*부터/);
            if (match4) {
                const startDate = match4[1];
                const parts = startDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 5. "처방기간" 키워드가 있고 ~ 표시가 있는 경우
            if (text.includes('처방기간') && (text.includes('~') || text.includes('∼') || text.includes('～'))) {
                // ~ 앞의 날짜 추출
                const beforeTilde = text.split(/[~∼～]/)[0];
                const match5 = beforeTilde.match(/(\d{4})[^\d]*(\d{1,2})[^\d]*(\d{1,2})/);
                if (match5) {
                    const year = match5[1];
                    const month = match5[2].padStart(2, '0');
                    const day = match5[3].padStart(2, '0');
                    // 유효한 날짜 범위 체크
                    if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                        parseInt(day) >= 1 && parseInt(day) <= 31) {
                        return `${year}-${month}-${day}`;
                    }
                }
            }

            // 6. 숫자 8자리 패턴 (YYYYMMDD) - ~ 앞에 있는 경우
            if (text.includes('~') || text.includes('∼') || text.includes('～')) {
                const beforeTilde = text.split(/[~∼～]/)[0];
                const match6 = beforeTilde.match(/(\d{4})(\d{2})(\d{2})/);
                if (match6) {
                    const year = match6[1];
                    const month = match6[2];
                    const day = match6[3];
                    if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                        parseInt(day) >= 1 && parseInt(day) <= 31) {
                        return `${year}-${month}-${day}`;
                    }
                }
            }

            return null;
        },

        // 산소치료 처방기간 종료일 추출 개선 - ~ 표시 기준 뒤쪽
        extractOxygenEndDate(text) {
            if (!text || typeof text !== 'string') return null;

            // 1. "2024-02-21 ~ 2025-02-20 까지" 패턴 (하이픈, ~ 포함)
            const match1 = text.match(/(\d{4}-\d{1,2}-\d{1,2})\s*[~∼～]\s*(\d{4}-\d{1,2}-\d{1,2})/);
            if (match1) {
                const endDate = match1[2];
                const parts = endDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 2. "2024.02.21 ~ 2025.02.20" 패턴 (점, ~ 포함)
            const match2 = text.match(/(\d{4}\.\d{1,2}\.\d{1,2})\s*[~∼～]\s*(\d{4}\.\d{1,2}\.\d{1,2})/);
            if (match2) {
                const endDate = match2[2].replace(/\./g, '-');
                const parts = endDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 3. "2024년 02월 21일 ~ 2025년 02월 20일" 패턴 (년월일, ~ 포함)
            const match3 = text.match(/[~∼～]\s*(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
            if (match3) {
                const year = match3[1];
                const month = match3[2].padStart(2, '0');
                const day = match3[3].padStart(2, '0');
                return `${year}-${month}-${day}`;
            }

            // 4. "2025-02-20까지" 또는 "2025-02-20 까지" 패턴
            const match4 = text.match(/(\d{4}-\d{1,2}-\d{1,2})\s*까지/);
            if (match4) {
                const endDate = match4[1];
                const parts = endDate.split('-');
                if (parts.length === 3) {
                    const year = parts[0];
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    return `${year}-${month}-${day}`;
                }
            }

            // 5. "처방기간" 키워드가 있고 ~ 표시가 있는 경우
            if (text.includes('처방기간') && (text.includes('~') || text.includes('∼') || text.includes('～'))) {
                // ~ 뒤의 날짜 추출
                const afterTilde = text.split(/[~∼～]/)[1];
                if (afterTilde) {
                    const match5 = afterTilde.match(/(\d{4})[^\d]*(\d{1,2})[^\d]*(\d{1,2})/);
                    if (match5) {
                        const year = match5[1];
                        const month = match5[2].padStart(2, '0');
                        const day = match5[3].padStart(2, '0');
                        // 유효한 날짜 범위 체크
                        if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                            parseInt(day) >= 1 && parseInt(day) <= 31) {
                            return `${year}-${month}-${day}`;
                        }
                    }
                }
            }

            // 6. 숫자 8자리 패턴 (YYYYMMDD) - ~ 뒤에 있는 경우
            if (text.includes('~') || text.includes('∼') || text.includes('～')) {
                const afterTilde = text.split(/[~∼～]/)[1];
                if (afterTilde) {
                    const match6 = afterTilde.match(/(\d{4})(\d{2})(\d{2})/);
                    if (match6) {
                        const year = match6[1];
                        const month = match6[2];
                        const day = match6[3];
                        if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                            parseInt(day) >= 1 && parseInt(day) <= 31) {
                            return `${year}-${month}-${day}`;
                        }
                    }
                }
            }

            // 7. "부터"와 "까지" 사이의 날짜 추출
            if (text.includes('부터') && text.includes('까지')) {
                const match7 = text.match(/부터[^\d]*(\d{4})[^\d]*(\d{1,2})[^\d]*(\d{1,2})[^\d]*까지/);
                if (match7) {
                    const year = match7[1];
                    const month = match7[2].padStart(2, '0');
                    const day = match7[3].padStart(2, '0');
                    if (parseInt(month) >= 1 && parseInt(month) <= 12 &&
                        parseInt(day) >= 1 && parseInt(day) <= 31) {
                        return `${year}-${month}-${day}`;
                    }
                }
            }

            return null;
        },

        // 서버에 청구 데이터 저장
        async saveClaimDataToServer(saveData) {
            try {
                let response;

                if (this.selectedClaimIndex >= 0) {
                    // 수정 모드
                    const claimId = this.claimHistory[this.selectedClaimIndex].id;
                    response = await this.$axios.put(
                        `${this.$apiUrls.hanqmedi}/oxygenClaimData/${claimId}`,
                        saveData
                    );
                } else {
                    // 새로 생성 모드
                    response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/oxygenClaimData`,
                        saveData
                    );
                }

                if (response.data && response.data.success) {
                    this.$swal.fire({
                        icon: 'success',
                        title: '저장 완료',
                        text: '청구 정보가 성공적으로 저장되었습니다.',
                        confirmButtonText: '확인'
                    });

                    // 청구 이력 새로고침
                    await this.loadClaimHistory();

                    // 이벤트 발생
                    this.$emit('claim-info-saved', {
                        orderId: this.orderId,
                        claimType: this.claimType,
                        prescriptionFile: this.prescriptionFile,
                        prescriptionData: saveData,
                        isEdit: this.selectedClaimIndex >= 0,
                        claimId: this.selectedClaimIndex >= 0 ? this.claimHistory[this.selectedClaimIndex].id : response.data.data.oxygenDataId,
                        claimStatus: {
                            hasClaimData: true,
                            currentStep: 0,
                            currentStepText: this.prescriptionData.status,
                            isCompleted: false
                        }
                    });

                    // 산소발생기 환자 목록 새로고침
                    window.dispatchEvent(new CustomEvent('refresh-oxygen-patients'));
                } else {
                    throw new Error(response.data.message || '저장에 실패했습니다.');
                }
            } catch (error) {
                console.error('청구 정보 저장 오류:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '저장 실패',
                    text: error.response?.data?.message || '저장 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 폼 초기화
        resetForm() {
            this.prescriptionData = {
                certificateName: '',
                cert_password: '',
                name: '',
                residentNumber: '',
                phone: '',
                diseaseName: '',
                diseaseCode: '',
                doctorLicense: '',
                specialistLicense: '',
                oxygenSdate: null,
                oxygenEdate: null,
                oxygenDate: null,
                receiptType: '',
                issuanceType: '',
                useHomeStartDate: null,
                useHomeEndDate: null,
                useMobileStartDate: null,
                useMobileEndDate: null,
                oxygenHomeGenerator: '',
                oxygenMobileGenerator: '',
                homeBillingCount: '',
                mobileBillingCount: '',
                cashReceipt: '',
                cashReceiptDate: null,
                cashReceiptAmount: '',
                taxInvoice: '',
                taxInvoiceDate: null,
                taxInvoiceAmount: '',
                bloodGasItem1: false,
                bloodGasItem2: false,
                bloodGasItem3: false,
                bloodGasItem4: false,
                bloodGasItem5: false,
                bloodGasItem6: false,
                bloodGasItem7: false,
                oxygenSaturationItem1: false,
                oxygenSaturationItem2: false,
                oxygenSaturationItem3: false,
                oxygenSaturationItem4: false,
                oxygenSaturationItem5: false
            };
            this.prescriptionFile = null;
            this.selectedClaimIndex = -1;
        },

        // 상병코드 포맷팅 (대문자 변환)
        formatDiseaseCode(event) {
            if (event && event.target) {
                // 입력된 값을 대문자로 변환
                const upperValue = event.target.value.toUpperCase();
                this.prescriptionData.diseaseCode = upperValue;
                // input 요소의 값도 직접 업데이트 (v-model과 동기화)
                event.target.value = upperValue;
            }
        },

        // 주민등록번호에 하이픈 추가 (통합)
        formatResidentNumberWithHyphens(value) {
            if (!value) return '';

            // 숫자만 추출
            const numbers = value.replace(/[^0-9]/g, '');

            if (numbers.length >= 7) {
                return numbers.substring(0, 6) + '-' + numbers.substring(6, 13);
            } else if (numbers.length >= 6) {
                return numbers.substring(0, 6) + '-' + numbers.substring(6);
            }

            return numbers;
        },

        // 전화번호에 하이픈 추가 (통합)
        formatPhoneWithHyphens(value) {
            if (!value) return '';

            // 숫자만 추출
            const numbers = value.replace(/[^0-9]/g, '');

            if (numbers.length >= 11) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3, 7) + '-' + numbers.substring(7, 11);
            } else if (numbers.length >= 7) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3, 7) + '-' + numbers.substring(7);
            } else if (numbers.length >= 3) {
                return numbers.substring(0, 3) + '-' + numbers.substring(3);
            }

            return numbers;
        },

        // 주민등록번호 포맷팅 (사용자 입력용)
        formatResidentNumber(event) {
            this.prescriptionData.residentNumber = this.formatResidentNumberWithHyphens(event.target.value);
        },

        // 전화번호 포맷팅 (사용자 입력용)
        formatPhoneNumber(event) {
            this.prescriptionData.phone = this.formatPhoneWithHyphens(event.target.value);
        },

        // "-" 제거 함수
        removeHyphens(value) {
            return value ? value.replace(/-/g, '') : '';
        },

        // 날짜 포맷팅 헬퍼 메서드들
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            // 한국 시간대로 표시
            return date.toLocaleDateString('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        },

        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return '';
            const start = new Date(startDate);
            const end = new Date(endDate);

            const startFormatted = start.toLocaleDateString('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });

            const endFormatted = end.toLocaleDateString('ko-KR', {
                timeZone: 'Asia/Seoul',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });

            return `${startFormatted} ~ ${endFormatted}`;
        },

        formatPrice(price) {
            return new Intl.NumberFormat('ko-KR').format(price);
        },
    },

    // 컴포넌트 해제 시 URL 정리
    beforeUnmount() {
        if (this.prescriptionPreviewUrl) {
            URL.revokeObjectURL(this.prescriptionPreviewUrl);
        }
    },
};
</script>